import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useUser, useClerk, useSession, SignIn } from "@clerk/clerk-react";
import { useSupabase } from "./supabaseClient";
import {
  MessageSquare, TrendingUp, FileText, Palette,
  Sparkles, Users, Send, Bot, User, X, Menu,
  CheckCircle, Circle, ChevronDown, ChevronRight,
  MessageCircle, BookOpen, Scroll, BarChart2, LogOut,
  Trash2, AlertTriangle, RotateCcw, Globe, Settings, ExternalLink,
  Lightbulb, Zap, Eye, Lock, Newspaper, GraduationCap, Copy, Check,
  Pencil, Download, Target, MoreHorizontal, Star, Award
} from "lucide-react";
import { C, FONT_STACK, tokenCSS } from "./constants/colors";
import { TERMS_VERSION } from "./constants/legal";
import {
  MODULE_OPENERS, MODULE_PROMPTS, MODULE_TRANSITIONS, MODULE_META, TABS,
  BLUEPRINT_LABELS, ACCELERATOR_MODULES, MOMENTUM_MODULES,
  IDEATION_MODULES, IDEATION_CONTEXT_EXPORTERS, IDEATION_CONTEXT_RECEIVERS,
  MOMENTUM_PLACEHOLDER_MODULES, JOURNEY_MODULES, ACCELERATOR_MODULE_LABELS,
} from "./constants/modules";
import { ARCHETYPE_MAP, ARCHETYPE_GROUPS } from "./constants/archetypes";
import { findStaleModules, rebuildQueueFor, buildNicheSnapshot } from "./constants/nicheSensitivity";
import { parseOutputs, detectCompletedPrompts, cleanMessageContent, extractLockedSections, getVisiblePrompts, restoreSectionValue, LOCK_FAIL, lockFailureReason } from "./utils/messages";
import { resolveOutputRows, sectionsAlreadyUnder, sectionKey } from "./utils/outputRows";
import { FontPreview, renderInline, renderMarkdown } from "./utils/markdown";
import { timeAgo, accessStatus } from "./utils/admin";
import { PanelModeProvider } from "./hooks/usePanelMode";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { useAskBrillChats, EARLIER, chatKeyOf, conversationIdFor, CHAT_MODULE_KEYS } from "./utils/askBrillChats";
import { whereLeftOff as findWhereLeftOff, placeOf } from "./utils/whereLeftOff";
import { ProgressBar } from "./components/ProgressBar";
import { OutputPanel } from "./components/OutputPanel";
import { BlueprintPanel } from "./components/BlueprintPanel";
import { ArchetypeResultsPanel } from "./components/ArchetypeResultsPanel";
import { WritingPanel } from "./components/WritingPanel";
import { SkillsPanel } from "./components/SkillsPanel";
import { BrandPanel } from "./components/BrandPanel";
import { BusinessPanel } from "./components/BusinessPanel";
import { BioPanel } from "./components/BioPanel";
import { MeFrame, ME_SCREENS } from "./components/MeFrame";
import { settingsColumns } from "./utils/settingsColumns";
import { brandAfterLock } from "./utils/brand";
import { LockedModulePanel } from "./components/LockedModulePanel";
import { PlaceholderModulePanel } from "./components/PlaceholderModulePanel";
import { JourneyBar } from "./components/JourneyBar";
import { BottomNav } from "./components/BottomNav";
import { ReviewWidget } from "./components/ReviewWidget";
import { TestimonialGatePanel } from "./components/TestimonialGatePanel";
import { LoginScreen } from "./components/LoginScreen";
import { NoAccessScreen } from "./components/NoAccessScreen";
import { ConsentScreen } from "./components/ConsentScreen";
import { UsageChart } from "./components/UsageChart";
import { UsageByUser } from "./components/UsageByUser";
import { AdminPanel } from "./components/AdminPanel";
import { SettingsPanel } from "./components/SettingsPanel";
import { CreditsBanner } from "./components/Credits";
import { Sidebar } from "./components/Sidebar";
import { ChatPanel } from "./components/ChatPanel";
import { NichePanel } from "./components/NichePanel";
import { NicheReviewPanel } from "./components/NicheReviewPanel";
import { NicheTriageDialog } from "./components/NicheTriageDialog";
import { MomentumHub, isAdminEmail, navIdFor, BUCKETS, toolStatus } from "./components/MomentumHub";
import { openableTools } from "./utils/brillTools";
import { useOffers } from "./utils/useOffers";
import { useMomentumCounts } from "./utils/momentumBuilt";
import { migrateOffersToSupabase, offerMigrationDone } from "./utils/migrateOffers";
import { InstallBanner } from "./components/InstallBanner";
import { useCanvaReturn } from "./utils/canva";

// ─── MAIN APP (authenticated) ─────────────────────────────────────────────────

function BrillApp() {
  const { user } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const { signOut } = useClerk();
  const getSupabase = useSupabase();
  /* Canva sends its Allow screen back to Brill's front page; this finishes the
     sign-in there and closes the Connect window (utils/canva.js). Up here with
     the other hooks — never below a return. */
  useCanvaReturn();
  const [active, setActive] = useState(() => localStorage.getItem("brill_last_tab") || "chat");
  useEffect(() => { localStorage.setItem("brill_last_tab", active); }, [active]);
  /* The last screen she opened that is not Ask Brill — what "Last time you
     were working in …" names (utils/whereLeftOff.js). Kept in BOTH places
     (her call, 2026-10-22: it should follow her between devices):
     localStorage answers at once on this browser, `settings.last_place`
     (supabase/0044) carries it to the next one. Each copy holds `at`, and the
     newer wins when settings load. One small write per screen she OPENS, and
     only when the place changed — never per render. Until 0044 has run the
     write fails quietly and this browser still remembers. */
  const [lastPlace, setLastPlace] = useState(() => {
    try { return JSON.parse(localStorage.getItem("brill_last_place") || "null"); } catch { return null; }
  });
  const writtenPlaceRef = useRef(null);
  /* Through a ref, not a dependency: `getSupabase` is a new function on
     renders, and as a dependency this re-ran — and re-tried a failing save —
     on every one of them (seen on localhost before 0044: 18 tries a minute). */
  const getSupabaseRef = useRef(getSupabase);
  getSupabaseRef.current = getSupabase;
  useEffect(() => {
    const found = placeOf(active, BUCKETS.flatMap((b) => b.items));
    if (!found) return;
    const place = { ...found, at: new Date().toISOString() };
    setLastPlace(place);
    try { localStorage.setItem("brill_last_place", JSON.stringify(place)); } catch { /* private window: the greeting just says less */ }
    if (!user?.id || writtenPlaceRef.current === place.id) return;
    writtenPlaceRef.current = place.id;
    (async () => {
      try {
        const sb = await getSupabaseRef.current();
        const { error } = await sb.from("settings").upsert(
          { clerk_user_id: user.id, last_place: place, updated_at: place.at },
          { onConflict: "clerk_user_id" });
        if (error) throw error;
      } catch (err) {
        /* Not retried until she opens another screen: one failed try per
           screen, never a loop. */
        console.warn("Last place not saved to settings (has 0044 run?):", err?.message || err);
      }
    })();
  }, [active, user?.id]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  /* Which tab the bottom bar asked the nav to show. A new object every time,
     so tapping the same button twice still lands (see Sidebar). */
  const [phaseRequest, setPhaseRequest] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [moduleMessages, setModuleMessages] = useState(() => {
    const init = {};
    TABS.forEach((id) => { init[id] = [{ role: "assistant", content: MODULE_OPENERS[id] }]; });
    return init;
  });
  const [moduleLoading, setModuleLoading] = useState({});
  const [moduleOutputs, setModuleOutputs] = useState({});
  /* Have her locked sections actually arrived from Supabase yet?
     `moduleOutputs` starts empty and fills asynchronously, and the lock badges
     now read from it -- so without this every section would render as "not
     saved" for a beat on load, which looks exactly like losing her work. Until
     this is true the UI keeps its old transcript-derived behaviour. */
  const [outputsLoaded, setOutputsLoaded] = useState(false);
  const [blueprints, setBlueprints] = useState({});
  const [rightPanel, setRightPanel] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  /* ASK BRILL CHATS (utils/askBrillChats.js, supabase/0042). `askKey` is the
     chat on screen: null is a new chat not yet started (the Welcome back
     screen), EARLIER is the old single thread, anything else a chat's id.
     A named chat's messages live in `askThreads`; the old thread stays in
     moduleMessages.chat, where it has always been. */
  const [askKey, setAskKey] = useState(null);
  const [askThreads, setAskThreads] = useState({});
  const askChats = useAskBrillChats(user?.id);
  /* Written BY KEY, captured when a message is sent: a reply that lands after
     she has switched chats still goes into the chat it answers. */
  const setAskMessagesFor = useCallback((key) => (msgs) => {
    if (key === EARLIER) setModuleMessages((prev) => ({ ...prev, chat: msgs }));
    else setAskThreads((prev) => ({ ...prev, [key]: msgs }));
  }, []);
  const startAskChat = (firstMessage, mode) => {
    const id = askChats.start(firstMessage, mode);
    setAskKey(id);
    return id;
  };
  const openAskChat = (key) => { setActive("chat"); setAskKey(key); setRightPanel(null); };
  const newAskChat = () => { setActive("chat"); setAskKey(null); setRightPanel(null); };
  const deleteAskChat = (key, title) => setConfirmDialog({
    title: "Delete this chat?",
    message: `“${title}” will be gone for good. Anything you saved to your Library stays where it is.`,
    confirmLabel: "Delete chat",
    danger: true,
    onConfirm: async () => {
      setConfirmDialog(null);
      if (!(await askChats.remove(key))) return;
      if (key === EARLIER) setModuleMessages((prev) => ({ ...prev, chat: [{ role: "assistant", content: MODULE_OPENERS.chat }] }));
      else setAskThreads((prev) => { const n = { ...prev }; delete n[key]; return n; });
      setAskKey((k) => (k === key ? null : k));
    },
  });
  // "light" | "dark" — swaps the canvas tokens only. The sidebar and panel
  // headers keep their fixed chrome palette, which is why this is "panel mode"
  // and not an app-wide dark mode.
  //
  // localStorage stays the seed value so the first paint never flashes the
  // wrong scope while Supabase loads; the stored row wins once it arrives.
  const [panelMode, setPanelMode] = useState(() => localStorage.getItem("brill_panel_mode") || "light");
  useEffect(() => { localStorage.setItem("brill_panel_mode", panelMode); }, [panelMode]);
  const [historyLoadFailed, setHistoryLoadFailed] = useState(false);
  const [userSettings, setUserSettings] = useState({});
  /* Offer Ladder state that isn't the offers themselves. `settingsLoaded` gates
     the ladder's automatic Accelerator pull: until we know whether offers_seeded
     is true, pulling could duplicate an offer she already has. It flips even when
     the row is absent (a brand-new coach has no settings row yet) — otherwise the
     pull would never run for anyone new. */
  const [offersSeeded, setOffersSeeded] = useState(false);
  /* Me → Writing: her own writing rules, as stored in settings.writing. The
     server reads the same column for itself when she drafts (api/chat.js), so
     this copy is only for the screen. */
  const [writing, setWriting] = useState(null);
  const [skills, setSkills] = useState(null);
  /* Same gate for Course's once-ever pull from her locked Curriculum. Modules
     have no natural key, so unlike website pages nothing in Postgres stops a
     second browser inserting the whole curriculum again — this flag is the
     guard. */
  const [courseSeeded, setCourseSeeded] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [offerMigrationTried, setOfferMigrationTried] = useState(() => offerMigrationDone());
  const [testimonialSubmitted, setTestimonialSubmitted] = useState(false);
  const [ideationTestimonialSubmitted, setIdeationTestimonialSubmitted] = useState(false);
  const [promptTrigger, setPromptTrigger] = useState(null);
  /* Bumped to focus the composer. `reviseTarget` rides alongside it and names
     the section she pressed Revise on, so ChatPanel can put the instruction
     into the NEXT message she sends — as a user turn. See buildReviseContext
     there, and the note on handleRevisePrompt below. */
  const [reviseFocusTrigger, setReviseFocusTrigger] = useState(0);
  const [reviseTarget, setReviseTarget] = useState(null);
  // ── Niche versions ──
  // The niche is a versioned record rather than a single locked object: old
  // niches stay on file as inactive rows, which is what makes a pivot reversible.
  //
  // `outputStamps` mirrors moduleOutputs but holds the niche_version_id each
  // section was locked under. Kept as a separate map on purpose — everything
  // downstream that reads moduleOutputs keeps working untouched.
  const [nicheVersions, setNicheVersions] = useState([]);
  const [outputStamps, setOutputStamps] = useState({});
  const activeNiche = nicheVersions.find((v) => v.is_active) || null;
  // Display only, for "you went from X to Y" — the most recently created niche
  // she isn't on any more.
  const previousNiche = nicheVersions.find((v) => !v.is_active) || null;

  /* A module's locked outputs: the saved (Supabase) rows over the
     parsed-from-history fallback. The one definition of that merge — Momentum's
     panels, the Social Generator and the blueprint all used to spell it out
     inline and could drift.

     Memoised for the same reason as staleByModule below. `parseOutputs` walks
     the module's whole message list, and these objects are handed to MomentumHub
     as props — a fresh object each render re-fires the effects that depend on
     them (the Offer Ladder's pull) on every keystroke. */
  const mergedOutputs = useCallback(
    (id) => ({ ...parseOutputs(moduleMessages[id] || []), ...(moduleOutputs[id] || {}) }),
    [moduleMessages, moduleOutputs],
  );
  const websiteOutputs = useMemo(() => mergedOutputs("website"), [mergedOutputs]);
  const offerOutputs = useMemo(() => mergedOutputs("offer"), [mergedOutputs]);
  /* Her whole foundation, the same reading MomentumHub is handed — for Ask
     Brill's coaches, which each take their slice of it (CONTEXT_SCOPES). */
  const foundationOutputs = useMemo(() => ACCELERATOR_MODULES.reduce((acc, id) => {
    const out = mergedOutputs(id);
    if (Object.keys(out).length > 0) acc[id] = out;
    return acc;
  }, {}), [mergedOutputs]);

  /* Records that the once-ever Accelerator pull has happened. Persists on its own
     rather than through saveSettings, for the same reason changePanelMode does:
     the upsert sets only the columns it names, so this can never cost her a
     display name or an archetype. Optimistic locally so the pull effect settles
     immediately instead of waiting on the round trip. */
  const markOffersSeeded = useCallback(async () => {
    setOffersSeeded(true);
    if (!user?.id) return;
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert({
        clerk_user_id: user.id,
        offers_seeded: true,
        updated_at: new Date().toISOString(),
      }, { onConflict: "clerk_user_id" });
      if (error) throw error;
    } catch (err) {
      // Worth shouting about: if this never lands, another browser re-runs the
      // pull and she ends up with two copies of her Signature Offer.
      console.error("Failed to persist offers_seeded:", err);
    }
  }, [user?.id, getSupabase]);

  /* Records that Course's once-ever pull has happened. Written on its own rather
     than through saveSettings, for the same reason as offers: the upsert sets
     only the columns it names, so this can never cost her a display name. */
  const markCourseSeeded = useCallback(async () => {
    setCourseSeeded(true);
    if (!user?.id) return;
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert({
        clerk_user_id: user.id,
        course_seeded: true,
        updated_at: new Date().toISOString(),
      }, { onConflict: "clerk_user_id" });
      if (error) throw error;
    } catch (err) {
      // If this never lands, another browser pulls her whole curriculum again.
      console.error("Failed to persist course_seeded:", err);
    }
  }, [user?.id, getSupabase]);

  const offerStore = useOffers({
    offerOutputs,
    offersSeeded,
    markSeeded: markOffersSeeded,
    // The pull stays off until we know the flag AND the localStorage lift has
    // been attempted. Both conditions guard against creating a duplicate.
    enabled: settingsLoaded && offerMigrationTried,
  });

  /* The one-time lift of browser-stored offers into Supabase. Idempotent — the
     rows keep their original `off_xxxxxxxx` ids, so a re-run upserts rather than
     duplicating. Runs once the table has loaded so it never races the read. */
  useEffect(() => {
    if (offerMigrationTried || !settingsLoaded || offerStore.loading || !user?.id) return;
    let cancelled = false;
    (async () => {
      const { moved, failed } = await migrateOffersToSupabase({
        save: offerStore.saveOffer,
        markSeeded: markOffersSeeded,
      });
      if (moved || failed) console.info(`Offer migration: ${moved} moved, ${failed} failed.`);
      // Set even when some failed — `migrateOffersToSupabase` only writes its own
      // done-flag on a clean run, so the retry happens on the next page load
      // rather than looping here.
      if (!cancelled) setOfferMigrationTried(true);
    })();
    return () => { cancelled = true; };
  }, [offerMigrationTried, settingsLoaded, offerStore.loading, user?.id, offerStore.saveOffer, markOffersSeeded]);

  /* Memoised because this walks every locked section the coach has, and because
     two of its outputs are handed to Sidebar — a fresh object each render would
     rerender the whole tree on every keystroke in a chat box, for a value that
     only changes when she locks something or switches niche.

     staleByModule is { [moduleKey]: { [sectionName]: "redo" | "check" } } and
     staleTierByModule is { [moduleKey]: "redo" | "check" }, together driving the
     sidebar flags: which sections to mark and in which colour, and what the module
     row above them says. Section and module are tracked separately because modules
     are mixed — My Visibility is amber apart from Content Pillars — and one colour
     for the lot would either hide a rebuild or invent three. */
  const { staleModules, staleCounts, staleByModule, staleTierByModule } = useMemo(() => {
    const modules = findStaleModules(outputStamps, activeNiche?.id);
    const byModule = {};
    const tierByModule = {};
    modules.forEach(({ moduleKey, tier, stale }) => {
      byModule[moduleKey] = Object.fromEntries(stale.map((s) => [s.name, s.tier]));
      tierByModule[moduleKey] = tier;
    });
    return {
      staleModules: modules,
      staleCounts: {
        // Full rebuilds, part-rebuilds and read-throughs counted apart, so the
        // summary can't say "5 modules to rebuild" when one of them is a single
        // red section among three amber ones.
        redo: modules.filter((m) => m.redoCount > 0 && m.checkCount === 0).length,
        mixed: modules.filter((m) => m.redoCount > 0 && m.checkCount > 0).length,
        check: modules.filter((m) => m.redoCount === 0).length,
      },
      staleByModule: byModule,
      staleTierByModule: tierByModule,
    };
  }, [outputStamps, activeNiche?.id]);
  const phase = user?.publicMetadata?.phase;
  // Cumulative access ladder: ideation < accelerator < momentum.
  // Momentum unlocks everything; accelerator unlocks Ideation + Accelerator; ideation unlocks the Ideation section only.
  const hasAcceleratorAccess = phase === "accelerator" || phase === "momentum";
  /* The Ask Brill greeting: where she was last and what is still open, counted
     from storage (utils/whereLeftOff.js). Recomputed only when her messages or
     locks change, never per keystroke. */
  const hasMomentumAccess = phase === "momentum";
  /* The Momentum tools this account can open, for Ask Brill to know about and
     offer a button into (utils/brillTools.js). Admin sees her admin-only ones. */
  const adminUser = isAdminEmail(user?.primaryEmailAddress?.emailAddress);
  const brillTools = useMemo(
    () => (hasMomentumAccess || adminUser ? openableTools(BUCKETS, (it) => toolStatus(it, adminUser)) : []),
    [hasMomentumAccess, adminUser]);
  const leftOff = useMemo(
    () => findWhereLeftOff({ moduleMessages, moduleOutputs, hasAcceleratorAccess, hasMomentumAccess, lastPlace }),
    [moduleMessages, moduleOutputs, hasAcceleratorAccess, hasMomentumAccess, lastPlace]);

  /* How many rows she has in each Momentum table, for the sidebar's progress bar
     and its per-tool ticks. Counts only — five `head` requests at sign-in, no
     rows returned and nothing re-fetched per render. See utils/momentumBuilt.js
     for why this can't read the stores: they live inside MomentumHub, which is
     only mounted while she is standing on a Momentum panel. */
  const { counts: momentumCounts, refresh: refreshMomentumCounts } = useMomentumCounts({ enabled: hasMomentumAccess });

  /* Asked again as she moves between panels, so writing her first page shows
     the tick beside Sites without a reload. A tick that quietly never appears
     is the failure mode check 2 exists for. `refresh` declines any run inside
     REFRESH_AFTER_MS of the last, so a walk down the nav is one run, not one
     per click. */
  useEffect(() => { refreshMomentumCounts(); }, [active, refreshMomentumCounts]);

  // ── Load settings from Supabase on mount ──
  useEffect(() => {
    if (!user?.id || !sessionLoaded || !session) return;
    const loadSettings = async () => {
      try {
        const sb = await getSupabase();
        const { data, error } = await sb
          .from("settings")
          .select("*")
          .eq("clerk_user_id", user.id)
          .single();
        if (error || !data) return;
        // Migrate old "Gold Dolphin" style values to named archetype
        const storedArchetype = data.archetype || "";
        const isNamed = storedArchetype in ARCHETYPE_MAP;
        const isPair = !isNamed && Object.values(ARCHETYPE_MAP).includes(storedArchetype);
        const archetypeName = isNamed ? storedArchetype
          : isPair ? Object.keys(ARCHETYPE_MAP).find(k => ARCHETYPE_MAP[k] === storedArchetype) || ""
          : "";
        const archetypePair = ARCHETYPE_MAP[archetypeName] || "";
        setUserSettings({
          displayName: data.display_name || "",
          archetype: archetypeName,
          archetypePair,
          emailUpdates: data.email_updates || false,
          emailReminders: data.email_reminders || false,
          /* supabase/0028, and absent until it is run — `select("*")` simply
             does not return a column that is not there, so these read as empty
             rather than throwing. The write degrades the same way. */
          businessName: data.business_name || "",
          audience: data.audience || "",
          logoUrl: data.logo_url || "",
          photoUrl: data.photo_url || "",
          /* Me → Brand (supabase/0037). Absent until it is run; reads as none.
             Carried IN userSettings so every tool that already has her
             settings has her colours — no second prop to forget. */
          brand: data.brand || null,
          /* Me → Business and Me → Bio (supabase/0038). Absent until it is
             run; read as empty. */
          website: data.website || "",
          bio: data.bio || "",
          /* Me → Business, the name her emails are signed with (supabase/0050).
             Absent until it is run; reads as empty. */
          signoff: data.signoff || "",
        });
        // Absent until the panel_mode column exists, and absent for anyone who
        // has never touched the toggle — in both cases localStorage stands.
        if (data.panel_mode === "light" || data.panel_mode === "dark") {
          setPanelMode(data.panel_mode);
        }
        // The once-ever Offer Ladder pull flag. Lives here rather than in
        // localStorage so a second browser doesn't re-run the pull and create a
        // duplicate Signature Offer.
        // Me → Writing (supabase/0031). Absent until it is run; reads as none.
        setWriting(data.writing || null);
        // Me → Skills (supabase/0033). Absent until it is run; reads as none.
        setSkills(data.skills || null);
        setOffersSeeded(Boolean(data.offers_seeded));
        setCourseSeeded(Boolean(data.course_seeded));
        // Where she was last (supabase/0044) — from another device, if newer
        // than what this browser remembers. Absent until 0044 is run.
        const remote = data.last_place;
        if (remote?.id && remote?.label) {
          setLastPlace((local) => {
            if (local?.at && remote.at && local.at >= remote.at) return local;
            try { localStorage.setItem("brill_last_place", JSON.stringify(remote)); } catch { /* fine */ }
            return remote;
          });
        }
      } catch (err) {
        console.error("Failed to load settings:", err);
      } finally {
        // Flips even on the early return above (no settings row yet) and on a
        // failure — the ladder must not stay blocked forever waiting on this.
        setSettingsLoaded(true);
      }
    };
    loadSettings();
  }, [user?.id]);

  const saveSettings = async (updates) => {
    setUserSettings((s) => ({ ...s, ...updates }));
    if (!user?.id) return;

    /* THE COLUMNS ARE NAMED, so a key nobody has added a column for updates the
       screen, says "Saved", and is gone on the next load. That is exactly what
       happened to businessName, audience, logoUrl and photoUrl the day they
       were added (2026-09-22) — the fields worked, the writes did not, and
       nothing said so.
       ONLY THE CHANGED ONES ARE WRITTEN (2026-10-13). This used to send every
       column from what the screen had loaded, so a save before her settings
       arrived blanked her display name, archetype, logo and photo links.
       `settingsColumns` holds the names and the reasoning; the upsert leaves
       every column it does not name exactly as it was. */
    /* `business` is supabase/0028's columns, split out so that if it has not
       been run, the retry below still saves her name and her archetype rather
       than failing the whole row. */
    const { basic, business } = settingsColumns(updates);
    const base = { clerk_user_id: user.id, ...basic, updated_at: new Date().toISOString() };

    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert({ ...base, ...business }, { onConflict: "clerk_user_id" });
      if (!error) return;
      if (!/column|schema cache/i.test(error.message || "")) throw error;
      console.warn("settings: supabase/0028 not run yet — saving everything but the business fields.");
      const retry = await sb.from("settings").upsert(base, { onConflict: "clerk_user_id" });
      if (retry.error) throw retry.error;
    } catch (err) {
      console.error("Failed to save settings:", err);
    }
  };

  // Panel mode persists on its own rather than through saveSettings, so that a
  // missing panel_mode column can only ever cost the theme — never a display
  // name or an archetype. The upsert sets only the columns it names, so the
  // rest of the row is untouched.
  /* Me → Writing saves ON ITS OWN, the way panel_mode does: an upsert that
     names only `writing`, so a 0031 she has not run yet can fail only this —
     never her name or her archetype. Returns whether it landed, because the
     screen says "That didn't save" in amber when it did not (rule 1). */
  const saveWriting = useCallback(async (next) => {
    setWriting(next);
    if (!user?.id) return false;
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert(
        { clerk_user_id: user.id, writing: next, updated_at: new Date().toISOString() },
        { onConflict: "clerk_user_id" },
      );
      if (error) { console.error("Failed to save writing:", error.message || error); return false; }
      return true;
    } catch (err) {
      console.error("Failed to save writing:", err);
      return false;
    }
  }, [user?.id]);

  /* Me → Skills saves ON ITS OWN, like Writing: an upsert naming only
     `skills`, so a 0033 she has not run can fail only this. */
  const saveSkills = useCallback(async (next) => {
    setSkills(next);
    if (!user?.id) return false;
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert(
        { clerk_user_id: user.id, skills: next, updated_at: new Date().toISOString() },
        { onConflict: "clerk_user_id" },
      );
      if (error) { console.error("Failed to save skills:", error.message || error); return false; }
      return true;
    } catch (err) {
      console.error("Failed to save skills:", err);
      return false;
    }
  }, [user?.id]);

  /* Me → Brand saves ON ITS OWN, like Writing and Skills: an upsert naming
     only `brand`, so a 0037 she has not run can fail only this. It also lands
     in userSettings, which is where every tool reads it from. */
  const saveBrand = useCallback(async (next) => {
    setUserSettings((s) => ({ ...s, brand: next }));
    if (!user?.id) return false;
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert(
        { clerk_user_id: user.id, brand: next, updated_at: new Date().toISOString() },
        { onConflict: "clerk_user_id" },
      );
      if (error) { console.error("Failed to save brand:", error.message || error); return false; }
      return true;
    } catch (err) {
      console.error("Failed to save brand:", err);
      return false;
    }
  }, [user?.id]);

  /* Me → Business's website and Me → Bio save ON THEIR OWN, like Skills: an
     upsert naming only that column, so a 0038 she has not run fails only
     this — in amber — never her name or her archetype. */
  const saveAboutField = useCallback(async (key, value) => {
    setUserSettings((s) => ({ ...s, [key]: value }));
    if (!user?.id) return false;
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert(
        { clerk_user_id: user.id, [key]: value || null, updated_at: new Date().toISOString() },
        { onConflict: "clerk_user_id" },
      );
      if (error) { console.error(`Failed to save ${key}:`, error.message || error); return false; }
      return true;
    } catch (err) {
      console.error(`Failed to save ${key}:`, err);
      return false;
    }
  }, [user?.id]);

  const changePanelMode = useCallback(async (mode) => {
    setPanelMode(mode);
    if (!user?.id) return;
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("settings").upsert({
        clerk_user_id: user.id,
        panel_mode: mode,
        updated_at: new Date().toISOString(),
      }, { onConflict: "clerk_user_id" });
      // PGRST204 = column not in the schema cache. Expected until the migration
      // runs; localStorage already holds the value, so there is nothing to do.
      if (error && error.code !== "PGRST204") throw error;
    } catch (err) {
      console.error("Failed to save panel mode:", err);
    }
  }, [user?.id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Load testimonial status from Supabase on mount ──
  useEffect(() => {
    if (!user?.id || !sessionLoaded || !session) return;
    const checkTestimonial = async () => {
      try {
        const sb = await getSupabase();
        const { data } = await sb
          .from("testimonials")
          .select("id")
          .eq("clerk_user_id", user.id)
          .limit(1);
        if (data && data.length > 0) setTestimonialSubmitted(true);
      } catch (err) {
        console.error("Failed to load testimonial status:", err);
      }
    };
    const checkIdeationTestimonial = async () => {
      try {
        const sb = await getSupabase();
        const { data } = await sb
          .from("testimonials")
          .select("id")
          .eq("clerk_user_id", user.id)
          .eq("trigger", "ideation_blueprint_gate")
          .limit(1);
        if (data && data.length > 0) setIdeationTestimonialSubmitted(true);
      } catch (err) {
        console.error("Failed to load ideation testimonial status:", err);
      }
    };
    checkTestimonial();
    checkIdeationTestimonial();
  }, [user?.id]);

  // ── Load conversation history from Supabase on mount ──
  // Paginated to handle accounts with >1000 messages (Supabase REST default cap).
  useEffect(() => {
    if (!user?.id || !sessionLoaded || !session || dataLoaded) return;
    const loadHistory = async () => {
      try {
        const sb = await getSupabase();
        const PAGE_SIZE = 1000;
        const allData = [];
        let from = 0;

        while (true) {
          const { data, error } = await sb
            .from("messages")
            /* Only what the grouping below reads — `*` carried every column of
               every message she has ever sent (security batch 6, 2026-10-22). */
            .select("module_key, role, content, created_at, conversation_id")
            .eq("clerk_user_id", user.id)
            .order("created_at", { ascending: true })
            .range(from, from + PAGE_SIZE - 1);

          if (error) {
            console.error("Failed to load history page:", error);
            setHistoryLoadFailed(true);
            setDataLoaded(true);
            return;
          }
          if (!Array.isArray(data) || data.length === 0) break;
          allData.push(...data);
          if (data.length < PAGE_SIZE) break;
          from += PAGE_SIZE;
        }

        /* Cleared HERE, on success, not by Try again: clearing it on the press
           would turn Send back on while the reload was still running. */
        setHistoryLoadFailed(false);
        if (allData.length === 0) { setDataLoaded(true); return; }

        const grouped = {};
        const threads = {};
        TABS.forEach((id) => { grouped[id] = []; });
        allData.forEach((row) => {
          /* A named chat of any kind (Ask Anything, Program, Mindset) is its own
             thread. The old FAQ and Mindset threads are still not loaded — they
             never were — and only the old Ask Anything thread is "Earlier". */
          if (CHAT_MODULE_KEYS.includes(row.module_key) && chatKeyOf(row.conversation_id, user.id) !== EARLIER) {
            const key = chatKeyOf(row.conversation_id, user.id);
            (threads[key] = threads[key] || []).push({ role: row.role, content: row.content, created_at: row.created_at });
            return;
          }
          if (grouped[row.module_key] !== undefined) {
            /* `created_at` rides along so the niche boundary can be found later — see
               withNicheBoundary in utils/messages.js. It is stripped again when the
               payload is built, so nothing extra reaches the API. Messages created
               during this session carry no timestamp, which is correct: they are
               happening now, and now is after any pivot. */
            grouped[row.module_key].push({ role: row.role, content: row.content, created_at: row.created_at });
          }
        });

        setAskThreads(threads);
        setModuleMessages((prev) => {
          const updated = { ...prev };
          TABS.forEach((id) => {
            if (Array.isArray(grouped[id]) && grouped[id].length > 0) {
              updated[id] = [
                { role: "assistant", content: MODULE_OPENERS[id] },
                ...grouped[id],
              ];
            }
          });
          return updated;
        });
      } catch (err) {
        console.error("History load error:", err);
        setHistoryLoadFailed(true);
      }
      setDataLoaded(true);
    };
    loadHistory();
  }, [user?.id, dataLoaded]);

  // ── Load locked outputs + niche versions from Supabase on mount ──
  //
  // Ordered on purpose: outputs first, then niches. Bootstrapping a coach's first
  // niche version needs to know whether she has locked work to stamp against it.
  //
  // Every niche read is optional. Until supabase/niche_versions.sql has been run
  // the column and table don't exist, and locking work has to keep working
  // regardless — so a failure there degrades to "no versioning yet", never to a
  // blank foundation.
  useEffect(() => {
    if (!user?.id || !sessionLoaded || !session) return;
    const loadOutputs = async () => {
      /* Declared out here so the catch can still show her what was read.
         Publishing used to happen the moment the rows arrived; Stage 3 moved it
         after the niche read, because the stamp now decides WHICH content she
         sees. That handed the throw path a new way to be frightening — anything
         failing in between would render her foundation EMPTY until she reloaded.
         Nothing is lost when that happens, but "all my work is gone" is the last
         thing this app should be able to say by accident. */
      let rows = null;
      try {
        const sb = await getSupabase();

        let versioningReady = true;
        const withStamp = await sb
          .from("outputs")
          .select("id, module_key, section_name, content, niche_version_id, updated_at")
          .eq("clerk_user_id", user.id);
        if (withStamp.error) {
          versioningReady = false;
          const plain = await sb
            .from("outputs")
            .select("id, module_key, section_name, content, updated_at")
            .eq("clerk_user_id", user.id);
          if (plain.error || !Array.isArray(plain.data)) return;
          rows = plain.data;
        } else {
          rows = Array.isArray(withStamp.data) ? withStamp.data : [];
        }

        /* THE ONE PLACE THAT ANSWERS "WHICH ROW?".

           Since Stage 3 a section can hold a row per niche she has built it
           under, and these two maps hold one value per section. That flatten is
           lossy — without a rule, whichever row PostgREST returned last would
           silently win and she would see a different version on different loads.
           `resolveOutputRows` is that rule, and it is the ONLY place it lives:
           the maps keep exactly the shape they have always had, which is why
           nothing downstream of here changes. See utils/outputRows.js.

           Nothing is set until the niche is known. It used to publish the
           outputs immediately and the stamps a round-trip later, which was
           harmless when the stamp was decoration; now the stamp decides WHICH
           CONTENT she sees, so setting them apart would render one niche's work
           and then swap it for another's. `outputsLoaded` stays false until
           both land, and every badge holds rather than guessing. */
        const publish = (activeNicheId) => {
          const { outputs, stamps } = resolveOutputRows(rows, activeNicheId);
          setModuleOutputs(outputs);
          setOutputStamps(stamps);
          setOutputsLoaded(true);
        };

        /* No versioning in this database at all, so every row is unstamped and
           the resolver has nothing to choose between. */
        if (!versioningReady) { publish(null); return; }

        const { data: niches, error: nicheErr } = await sb
          .from("niche_versions")
          .select("id, label, statement, snapshot, is_active, created_at, activated_at")
          .eq("clerk_user_id", user.id)
          .order("created_at", { ascending: false });
        /* Her niche is unreadable, so which row is current cannot be decided.
           Publish anyway with no active niche — the resolver falls back to the
           unstamped row and then to her most recent work, which is the honest
           answer to "we could not tell". Returning here would leave her
           foundation blank. */
        if (nicheErr) { publish(null); return; }

        let versions = Array.isArray(niches) ? niches : [];

        /* Bootstrap. A coach who locked work before versioning existed has no
           niche row and a NULL stamp on every section. Give her a first version
           named from her locked niche statement — without this her entire back
           catalogue would flag as stale the day she first pivots.

           NO LONGER GATED ON HAVING LOCKED WORK (2026-08-26). It used to require
           `Object.keys(grouped).length > 0`, which meant a coach who had locked
           nothing yet got no niche row — so `activeNiche` was null when she
           locked her FIRST section, and `handleOutputLocked` wrote a null stamp.
           The next page load then created the version and backfilled the stamp,
           so the null healed itself and never showed up in a query.

           It is harmless today and fatal later: the moment `niche_version_id`
           joins the unique key, NULL is not equal to NULL in a Postgres unique
           index, so two null-stamped rows for one section both become legal. And
           if the column is made NOT NULL instead, that first lock fails outright
           and she loses it. Every new account walked this path.

           Cost of moving the gate: a niche row for someone who signs up and does
           nothing. Untidy, harmless, and it stops the null at source.
           See docs/stage-2-preflight.md. */
        if (versions.length === 0) {
          /* Resolved with no active niche, because there is none yet — that is
             the branch this block exists to fix. With no versions there is at
             most one row per section anyway, so the resolver has nothing to
             choose between and this is just the flatten. */
          const firstOutputs = resolveOutputRows(rows, null).outputs;
          const { data: created } = await sb
            .from("niche_versions")
            .insert({
              clerk_user_id: user.id,
              label: "My first niche",
              statement: firstOutputs.mission?.["Niche Statement"] || null,
              snapshot: buildNicheSnapshot(firstOutputs),
              is_active: true,
              activated_at: new Date().toISOString(),
            })
            .select()
            .single();
          if (created) versions = [created];
        }

        /* Self-heal an interrupted niche change. handleCreateNicheVersion has to
           deactivate the old row before inserting the new one — the one-active
           index forbids the reverse order — so a crash, a closed tab or a failed
           insert in that window leaves every version inactive. Nothing would ever
           repair it: the bootstrap above only fires when there are no versions at
           all, so activeNiche would stay null and every subsequent lock would save
           unstamped. Reactivate the most recent and carry on. */
        if (versions.length > 0 && !versions.some((v) => v.is_active)) {
          const newest = versions[0]; // ordered created_at desc
          const activatedAt = new Date().toISOString();
          await sb.from("niche_versions")
            .update({ is_active: true, activated_at: activatedAt })
            .eq("id", newest.id);
          versions = versions.map((v) =>
            v.id === newest.id ? { ...v, is_active: true, activated_at: activatedAt } : v);
        }

        /* Adopt the unstamped rows into her active niche.

           THIS UPDATE STOPPED BEING SAFE IN STAGE 3, and it failed silently:
           there was no error check here at all. `is null` matched every
           unstamped row, and if a section ALSO had a row under the active niche,
           stamping the old one would put two rows on the same key — a unique
           violation that rolls the whole statement back. One section could
           therefore stop the backfill for all of them, with nothing on screen.

           So it targets rows by `id`, skipping any section that already has an
           active-niche row. Those are left unstamped: the resolver puts them
           behind the active row anyway (branch 2 loses to branch 1), so she sees
           the right thing, and merging them would mean choosing whose content to
           throw away. Deleting her work to tidy an index is not a trade this
           makes. */
        const active = versions.find((v) => v.is_active);
        const orphans = active
          ? rows.filter((r) => !r.niche_version_id && r.id)
          : [];
        if (orphans.length) {
          const taken = sectionsAlreadyUnder(rows, active.id);
          const adoptable = orphans.filter(
            (r) => !taken.has(sectionKey(r.module_key, r.section_name)));
          if (adoptable.length) {
            const { error: stampErr } = await sb.from("outputs")
              .update({ niche_version_id: active.id })
              .in("id", adoptable.map((r) => r.id));
            /* Only believe it locally if the database took it. The maps are
               built from `rows` below, so a failed update now shows her the
               unstamped reading — which is what is actually stored. */
            if (stampErr) console.error("Failed to stamp unstamped outputs:", stampErr);
            else adoptable.forEach((r) => { r.niche_version_id = active.id; });
          }
        }

        setNicheVersions(versions);
        publish(active?.id || null);
      } catch (err) {
        console.error("Failed to load outputs:", err);
        /* Marked loaded even on failure. Staying false forever would leave every
           badge frozen on the transcript-derived reading with no way out; the
           lock states will read UNSAVED, which is at least honest about the fact
           that we could not confirm anything is stored.

           If the rows themselves arrived and something later failed, show them.
           Resolved with no active niche, which is the truth — we could not
           establish one — so a section falls back to its unstamped row and then
           to her most recent work, and reads "Past locked" rather than vanishing. */
        if (rows) {
          const { outputs, stamps } = resolveOutputRows(rows, null);
          setModuleOutputs(outputs);
          setOutputStamps(stamps);
        }
        setOutputsLoaded(true);
      }
    };
    loadOutputs();
  }, [user?.id]);

  /* ── Rebuilding a module after a niche change ──────────────────────────────

     The ordered walk through one module, one section at a time.

     It exists because the alternative doesn't work. Reopening single sections
     from the review screen drops her into the middle of a module, and Brill's
     guide — which ends nearly every section with "then move to X" — carries on
     from wherever it landed. A coach who pivoted mid-module reported exactly
     that: she walked My Market and was dropped into Discovery Interview
     Questions, because the two sections above it were locked and nothing had told
     Brill they were built for a client she no longer serves.

     So the running order lives here rather than in the prompt. The queue is the
     module's own order from MODULE_PROMPTS, and the app opens each section only
     once the one before it has actually locked — which is the only honest signal
     that a step is finished, since Brill *offering* to lock is not.

     Sections already locked on the current niche stay in the queue on purpose.
     Stepping over them silently is the thing that made this feel like it was
     jumping; the walk stops, shows her what's there and asks. The only sections
     skipped without a word are ones she clicked "Still true" on, because that was
     her decision and asking again is just friction.

     A ref rather than state: nothing renders from it, and it's read inside an
     async reply handler where a stale closure would stall the run. */
  const rebuildRunRef = useRef(null);
  const [clearedSections, setClearedSections] = useState({});
  /* Set when a module rebuild reaches the end of its queue. Holds only the module
     she just finished; which module comes next is worked out at render time off
     staleModules, because finishing this one changes what is left. */
  const [rebuildNext, setRebuildNext] = useState(null);
  /* AFTER A BRAND LOCK (her rule, 2026-10-12): "do you want me to update your
     brand colors in Me?" — the question, set when the lock SAVES, answered on
     a card under the chat. Nothing changes in Me until she says yes. Read
     from a ref because the lock handler's closure would hold a stale brand. */
  const [brandAsk, setBrandAsk] = useState(null);
  const brandRef = useRef(null);
  brandRef.current = userSettings?.brand || null;

  /* "Still true" is an answer about one specific change, not a permanent
     exemption — so pivoting again, or rolling back, puts those sections back in
     front of her. Clearing on the niche id covers both. */
  useEffect(() => { setClearedSections({}); }, [activeNiche?.id]);

  const runRebuildStep = useCallback((moduleKey, queue, index) => {
    if (index >= queue.length) {
      rebuildRunRef.current = null;
      /* One module at a time, and the next one is offered rather than entered.
         Rolling straight on would put her in a second module she never agreed to
         — and she may well want to stop after one. Only the module she just
         finished is recorded; the next one is worked out at render time, because
         finishing this module changes what is left. */
      setRebuildNext({ from: moduleKey });
      setModuleMessages((prev) => ({
        ...prev,
        [moduleKey]: [...(prev[moduleKey] || []), {
          role: "assistant",
          content: [
            `**That's ${MODULE_META[moduleKey]?.label || moduleKey} rebuilt.**`,
            "",
            `Every section in here is now built on ${activeNiche?.label || "your current niche"}. If anything still reads like the old version, open it from the sidebar and we'll take another pass.`,
          ].join("\n"),
        }],
      }));
      return;
    }

    rebuildRunRef.current = { moduleKey, queue, index };
    const sectionName = queue[index];
    const stamp = outputStamps[moduleKey]?.[sectionName];
    const builtOn = nicheVersions.find((v) => v.id === stamp);
    /* Three different asks, and Brill needs to be told which one this is —
       rebuild it, confirm it, or write it for the first time. */
    const state = !stamp ? "fresh" : stamp === activeNiche?.id ? "current" : "stale";

    setPromptTrigger({
      moduleId: moduleKey,
      promptName: sectionName,
      ts: Date.now(),
      redo: {
        oldNiche: state === "stale" ? (builtOn?.statement || builtOn?.label || null) : null,
        newNiche: activeNiche?.statement || activeNiche?.label || null,
        state,
        step: { index: index + 1, total: queue.length },
      },
    });
  }, [outputStamps, nicheVersions, activeNiche]);

  const handleRebuildModule = useCallback((moduleKey, justCleared = []) => {
    /* justCleared comes straight from the triage dialog. Its "Still true" marks are
       applied in the same tick as this call, and setClearedSections will not have
       landed yet — reading state alone would walk her into sections she has just
       waved through. */
    const cleared = [...(clearedSections[moduleKey] || []), ...justCleared];
    const queue = rebuildQueueFor(moduleKey, outputStamps, cleared);
    if (!queue.length) return;
    const label = MODULE_META[moduleKey]?.label || moduleKey;

    setRebuildNext(null);
    setRightPanel(null);
    setActive(moduleKey);
    /* Her briefing, not Brill's. An assistant message cannot instruct the model —
       it reads as something Brill already said and the module guide reasserts
       itself over it. The instruction goes in the user turn runRebuildStep fires. */
    setModuleMessages((prev) => ({
      ...prev,
      [moduleKey]: [...(prev[moduleKey] || []), {
        role: "assistant",
        content: [
          `**Rebuilding ${label}${activeNiche?.label ? ` for ${activeNiche.label}` : ""}.**`,
          "",
          /* The whole running order, not the first two and "and on down". She has
             to be able to see the shape of what she just agreed to — five named
             sections is a morning, and an unnamed remainder is a mountain. */
          `We'll go from the top, in order. One at a time, and I'll wait for you at each one.`,
          "",
          ...queue.map((name, i) => `${i + 1}. ${name}`),
          "",
          "Where something already holds up, say so and we'll move on — I'll show you what's there before anything changes. Nothing is overwritten until you lock it.",
        ].join("\n"),
      }],
    }));
    runRebuildStep(moduleKey, queue, 0);
  }, [outputStamps, clearedSections, activeNiche, runRebuildStep]);

  /* Triage: the "!" on a sidebar module row, where the thought actually occurs to
     her — she's looking at My Visibility, she knows her Sales Path didn't change,
     and she wants to say so without opening the niche review and hunting for it.
     Holds a module key; the dialog reads the rest out of staleModules. */
  const [triageModule, setTriageModule] = useState(null);


  /* A lock that did not reach the database.
   *
   * Until now this path logged to the console and said nothing. That was
   * survivable only because `mergedOutputs` fell back to the transcript, so a
   * failed write still LOOKED fine everywhere — the content came back from the
   * conversation instead of the row. Once that fallback goes, a failed lock is
   * simply lost work, and losing it quietly is the worst version of this bug.
   *
   * Momentum has said this for a while: "the read-out goes amber and names the
   * reason when a save is blocked, because Saved and silence look identical."
   * This is the Accelerator's version of that rule.
   *
   * Holds the content too, so she can press the button again rather than
   * getting Brill to write the whole section a second time. */
  const [lockFailure, setLockFailure] = useState(null);

  // The single chokepoint where anything gets locked — so it's the only place
  // that needs to record which niche the work was built on.
  const handleOutputLocked = useCallback(async (moduleKey, sectionName, content) => {
    /* Any lock in this module ends an open revision — not just a lock of the
       revised section. Brill can resolve a revision by locking something else,
       and a revision left open forever would suppress this module's exits
       forever, which is the opposite failure to the one being fixed. */
    setReviseTarget((t) => (t && t.moduleId === moduleKey ? null : t));
    const nicheId = activeNiche?.id || null;
    /* What to put back if the write fails. A section locked for the first time
       has no previous value, and `undefined` is the honest restore for that —
       see revertLock below, which deletes the key rather than writing undefined
       into it, because a key holding undefined still reads as present. */
    const priorContent = moduleOutputs[moduleKey]?.[sectionName];
    const priorStamp = outputStamps[moduleKey]?.[sectionName];

    setModuleOutputs((prev) => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || {}), [sectionName]: content },
    }));
    setOutputStamps((prev) => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || {}), [sectionName]: nicheId },
    }));

    /* Put the optimistic update back. Without this the tick stays on and the
       amber strip contradicts it — two signals disagreeing about one section,
       which is the exact disease being cured. The tick is the claim; if the
       write failed, the claim was false and has to come off. */
    const revertLock = (reason = LOCK_FAIL.UNKNOWN) => {
      setModuleOutputs((prev) => restoreSectionValue(prev, moduleKey, sectionName, priorContent));
      setOutputStamps((prev) => restoreSectionValue(prev, moduleKey, sectionName, priorStamp));
      setLockFailure({ moduleKey, sectionName, content, reason });
    };

    /* A rebuild advances from here and nowhere else. "The section locked" is the
       only honest signal a step is finished — Brill offering to lock is not, and
       neither is her replying. Tolerant of a reply that locks more than one
       section: jump past whatever it locked rather than stalling on a step that
       has already gone by. */
    const run = rebuildRunRef.current;
    if (run && run.moduleKey === moduleKey) {
      const at = run.queue.indexOf(sectionName);
      if (at >= run.index) runRebuildStep(moduleKey, run.queue, at + 1);
    }

    /* Signed out mid-session. Nothing can be written, so this is a failure like
       any other — it used to return silently and leave the tick on.

       THE ONLY REVERT THAT LOGGED NOTHING. Every other path here prints
       "Failed to save output"; this one printed nothing at all, so a lock lost
       this way left no trace in the console either. That cost real time on
       2026-09-01: a console was read looking for the reason a lock failed, and
       an empty console could not rule this out. */
    if (!user?.id) {
      console.error("Failed to save output: signed out, no user id");
      revertLock(LOCK_FAIL.SIGNED_OUT);
      return;
    }
    try {
      const sb = await getSupabase();
      const base = {
        clerk_user_id: user.id, module_key: moduleKey, section_name: sectionName,
        content, updated_at: new Date().toISOString(),
      };
      /* THE LINE STAGE 3 IS ABOUT.
         The conflict target was `(clerk_user_id, module_key, section_name)`, so
         re-locking a section under a NEW niche found the old row and overwrote
         it — her previous niche's work gone, silently, with no undo. With the
         niche in the key, that same lock finds nothing to conflict with and
         inserts a second row. Re-locking under the SAME niche still overwrites,
         deliberately: that is an edit, not a new version.

         Postgres needs a unique index matching this target EXACTLY, and
         PostgREST sends it as a bare column list — which is why supabase/0013
         exists. The `coalesce` index from Stage 2 cannot be named here.
         Deploying this against a database without 0013 fails every lock. */
      const opts = { onConflict: "clerk_user_id,module_key,section_name,niche_version_id" };
      const { error } = await sb.from("outputs").upsert({ ...base, niche_version_id: nicheId }, opts);
      /* Retry without the stamp only when the column genuinely isn't there — i.e.
         the migration hasn't been run. Retrying on *any* error would quietly drop
         the stamp on a transient network or RLS failure, and a silently unstamped
         section is invisible until it fails to flag months later. Losing the stamp
         is recoverable; losing her locked work is not, so the retry stays.

         THE RETRY KEEPS THE NARROW TARGET, and that is not an oversight. It only
         fires when `niche_version_id` does not exist as a column — a database
         from before versioning — and such a database has no wide index either,
         so retrying against the wide target would fail on the conflict target
         instead and lose the lock for a second, unrelated reason. A
         pre-migration database keeps pre-migration behaviour. */
      if (error) {
        const columnMissing = /niche_version_id/i.test(error.message || "")
          || error.code === "PGRST204" || error.code === "42703";
        if (columnMissing) {
          /* The retry was silent too. If the stampless write also fails there is
             genuinely nothing in the database and she has to be told. */
          const retry = await sb.from("outputs").upsert(base,
            { onConflict: "clerk_user_id,module_key,section_name" });
          if (retry.error) {
            console.error("Failed to save output:", retry.error);
            revertLock(lockFailureReason(retry.error));
            return;
          }
        } else {
          console.error("Failed to save output:", error);
          revertLock(lockFailureReason(error));
          return;
        }
      }
      /* Got here, so a row exists. Clear any earlier failure for this section —
         a stale amber strip about work that has since saved is its own lie. */
      setLockFailure((prev) =>
        prev && prev.moduleKey === moduleKey && prev.sectionName === sectionName ? null : prev);

      /* The niche statement is locked fourth of five in My Mission, so it often
         lands *after* her first niche version was created — and the bootstrap
         only ever runs once. Fill the statement in the first time it appears, or
         the niche panel shows a bare label forever.
       *
       * IT USED TO SAY `&& !activeNiche.statement` — fill it ONLY when null.
       * That was written as a backfill and it silently made a revision
       * impossible: once the field held anything, every later re-lock of her
       * Niche Statement skipped this write.
       *
       * A live client hit it hard. Her active version kept an 11 Aug statement
       * about "parent, career, general coordinator for the household" while she
       * went on to lock a completely different niche on 14 Aug and again on 21
       * Aug. `buildMomentumContext` injects THIS field as "Her current niche
       * is:" — so for two weeks every Momentum tool and every niche context
       * block was told she served a client she had moved on from, while the
       * newer statements sat in `outputs` where the niche panel never looks.
       *
       * Now it tracks the section. Re-locking her Niche Statement is her
       * refining the niche she is ON, not declaring a new one — a genuine pivot
       * creates a new version through the niche panel, and this only ever
       * touches the active row, so a rolled-back version keeps its own text. */
      /* A brand lock that differs from Me → Brand (or fills an empty one)
         ASKS — her rule. Only once the row exists: a failed save asks nothing. */
      if (moduleKey === "voice") {
        const ask = brandAfterLock(sectionName, content, brandRef.current);
        if (ask) setBrandAsk({ ...ask, moduleKey });
      }

      if (moduleKey === "mission" && sectionName === "Niche Statement"
          && activeNiche && activeNiche.statement !== content) {
        await sb.from("niche_versions").update({ statement: content }).eq("id", activeNiche.id);
        setNicheVersions((prev) => prev.map((v) =>
          v.id === activeNiche.id ? { ...v, statement: content } : v));
      }
    } catch (err) {
      console.error("Failed to save output:", err);
      revertLock(lockFailureReason(err));
    }
  }, [user?.id, activeNiche, runRebuildStep, moduleOutputs, outputStamps]);

  // ── Reset a single module ──
  const handleResetModule = useCallback((moduleId) => {
    const meta = MODULE_META[moduleId];
    /* SAY WHAT SURVIVES, NOT JUST WHAT GOES. Since Stage 3 this erases the
       active niche's locked sections and leaves every other niche's copies
       standing — but the old copy said only "erase all your conversation
       history", which named neither. She would either not know her other niches
       were safe, or not realise the chat was going with it.

       The chat is the asymmetric part and the reason this is worth spelling out:
       a message is not stamped to a niche, so there is nothing to filter on and
       the whole module's history goes, every niche's. Roll back afterwards and
       the locked work returns; the conversation that produced it does not.

       With no active niche there is no per-niche split — the delete drops its
       filter — so the copy drops the promise with it rather than making one
       that is not true. */
    /* Trailing punctuation stripped because she names her own niches and they do
       not always end tidily — a live one is "Emowering women through self
       defense." and quoting it verbatim rendered `defense.".` */
    const nicheName = activeNiche?.label?.trim().replace(/[.,;:\s]+$/, "");
    setConfirmDialog({
      title: `Reset ${meta.label}?`,
      message: nicheName
        ? `Your chat history in ${meta.label} will be gone, and so will the sections you locked under "${nicheName}". Anything you locked under your other niches stays exactly as it is. This cannot be undone.`
        : `This will permanently erase all your ${meta.label} conversation history and locked sections. This cannot be undone.`,
      confirmLabel: "Yes, erase it",
      danger: true,
      onConfirm: async () => {
        setConfirmDialog(null);
        setModuleMessages((prev) => ({
          ...prev,
          [moduleId]: [{ role: "assistant", content: MODULE_OPENERS[moduleId] }],
        }));
        /* A rebuild of the module she just erased has nothing left to walk, and
           leaving the ref set would have the next lock in here fire a step out of
           a run she abandoned. */
        if (rebuildRunRef.current?.moduleKey === moduleId) rebuildRunRef.current = null;
        setBlueprints((prev) => { const n = { ...prev }; delete n[moduleId]; return n; });
        setModuleOutputs((prev) => { const n = { ...prev }; delete n[moduleId]; return n; });
        if (active === moduleId) setRightPanel(null);
        try {
          const sb = await getSupabase();
          /* THE ACTIVE NICHE'S ROWS ONLY — her call, 2026-08-30: "the active
             niche would be more useful." Before Stage 3 a section had one row
             and this deleted it; now an unfiltered delete would take every
             niche's copy with it, which is the overwriting Stage 3 exists to
             end, done deliberately and with a confirm dialog on top.

             NULL-STAMPED ROWS GO TOO, and that is a decision rather than an
             oversight. Unstamped means pre-versioning and the whole app reads it
             as CURRENT — so leaving it would have her erase a module and watch
             the same content come back on the next load. What she pressed was
             "erase what I have now".

             With no active niche there is no per-niche split to respect, so the
             filter is dropped and this behaves exactly as it did before. */
          const outputDelete = sb.from("outputs").delete()
            .eq("clerk_user_id", user.id).eq("module_key", moduleId);
          await Promise.all([
            sb.from("messages").delete().eq("clerk_user_id", user.id).eq("module_key", moduleId),
            activeNiche?.id
              ? outputDelete.or(`niche_version_id.eq.${activeNiche.id},niche_version_id.is.null`)
              : outputDelete,
          ]);

          /* Re-read what survived. An older niche's copy of a section is still
             in the table on purpose, and the resolver will surface it as "Past
             locked" on the next load — so without this the module reads empty
             now and repopulates after a refresh, which looks like the erase
             failing. One query, on an action she takes rarely. */
          const { data: left } = await sb.from("outputs")
            .select("id, module_key, section_name, content, niche_version_id, updated_at")
            .eq("clerk_user_id", user.id).eq("module_key", moduleId);
          const { outputs, stamps } = resolveOutputRows(left, activeNiche?.id);
          setModuleOutputs((prev) => {
            const n = { ...prev };
            if (outputs[moduleId]) n[moduleId] = outputs[moduleId]; else delete n[moduleId];
            return n;
          });
          setOutputStamps((prev) => {
            const n = { ...prev };
            if (stamps[moduleId]) n[moduleId] = stamps[moduleId]; else delete n[moduleId];
            return n;
          });
        } catch (err) {
          console.error("Reset module error:", err);
        }
      },
    });
  /* `label` as well as `id`: the dialog names the niche, and she can rename a
     niche without changing which one is active — the copy would go stale. */
  }, [user?.id, active, activeNiche?.id, activeNiche?.label]);

  // ── Reset all modules ──
  const handleResetAll = useCallback(() => {
    setConfirmDialog({
      title: "Erase everything?",
      /* WHAT IT ACTUALLY DELETES. This used to say "conversation history",
         which is true and badly incomplete: it also clears `outputs` — every
         locked section, My Vision through the brand palette, the foundation
         every Momentum tool reads from. Her pages, offers and decks survive in
         their own tables and are left pointing at work that no longer exists.
         Naming it is the difference between a warning and a formality. */
      message: "This erases every conversation AND everything you have locked — My Vision, My Client, your offer, your brand palette, all of it. Your Momentum pages and offers stay, but the work they were built from will be gone. It cannot be undone and there is no backup.",
      confirmLabel: "Erase everything",
      /* Typed, not clicked. A dialog you can dismiss by reflex is no guard on
         something irreversible. */
      confirmPhrase: "DELETE EVERYTHING",
      danger: true,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const sb = await getSupabase();
          await Promise.all([
            sb.from("messages").delete().eq("clerk_user_id", user.id),
            sb.from("outputs").delete().eq("clerk_user_id", user.id),
            /* The chats' names. Their messages went with the line above; an
               error here (0042 not run yet) changes nothing she can see. */
            sb.from("ask_brill_chats").delete().eq("clerk_user_id", user.id),
          ]);
        } catch (err) {
          console.error("Reset all error:", err);
        }
        const init = {};
        TABS.forEach((id) => { init[id] = [{ role: "assistant", content: MODULE_OPENERS[id] }]; });
        setModuleMessages(init);
        setAskThreads({});
        setAskKey(null);
        askChats.clear();
        setBlueprints({});
        setModuleOutputs({});
        setRightPanel(null);
      },
    });
  }, [user?.id]);

  const handleViewOutput = (moduleId, promptName, content) => {
    setActive(moduleId);
    setRightPanel({ type: "output", moduleId, promptName, content });
  };

  const handleStartPrompt = (moduleId, promptName) => {
    setActive(moduleId);
    // Moving to another section is an answer: the revision is over, however it ended.
    setReviseTarget(null);
    setPromptTrigger({ moduleId, promptName, ts: Date.now() });
  };

  const handleRevisePrompt = (moduleId, promptName) => {
    setRightPanel(null);
    setActive(moduleId);
    const current = moduleOutputs[moduleId]?.[promptName]
      || parseOutputs(moduleMessages[moduleId] || [])[promptName];
    const body = current
      ? `Here's your locked **${promptName}**:\n\n${current}\n\nWhat would you like to change or refine? Tell me and I'll rewrite it and lock the updated version.`
      : `Let's revisit **${promptName}**. Tell me what you'd like to change or refine and we'll update it together.`;
    setModuleMessages((prev) => ({
      ...prev,
      [moduleId]: [...(prev[moduleId] || []), { role: "assistant", content: body }],
    }));
    /* WHY THIS EXISTS (2026-08-27). The message above is for HER — it shows her
       what she has so she can say what to change. It cannot instruct Brill.
       An assistant message reads to the model as something it already said, so
       it defers to the module's own prescriptive guide and carries on running
       the module in order instead of revising. That is exactly the bug the Redo
       flow had, and the shared preamble's REVISE FLOW rule was written to
       compensate for it — a prompt request competing with the module script,
       which the script tends to win.
       Naming the section here lets ChatPanel put the real instruction into the
       next message she sends, as a USER turn, which is the only kind Brill
       treats as an instruction. */
    setReviseTarget({ moduleId, promptName });
    setReviseFocusTrigger((n) => n + 1);
  };

  /* "Change the wording in My Mission" from the niche panel. Navigating there
     alone dropped her into the thread with nothing to respond to, so this runs
     the same revise flow the Revise button on Niche Statement runs — then offers
     the bigger door, because someone who came from the niche panel may want to
     rebuild the niche rather than reword it. */
  const handleReviseNicheStatement = useCallback(() => {
    setRightPanel(null);
    setActive("mission");
    const current = moduleOutputs.mission?.["Niche Statement"]
      || parseOutputs(moduleMessages.mission || [])["Niche Statement"];
    const body = [
      current
        ? `Here's your locked **Niche Statement**:\n\n${current}\n\nWhat would you like to change or refine? Tell me and I'll rewrite it and lock the updated version.`
        : `Let's write your **Niche Statement**. Tell me who you serve and what you get them to, and we'll shape it together.`,
      "",
      "If your niche has moved further than the wording, we can go back through My Mission from the beginning — the one problem, who you're for, the transformation promise — and rebuild it properly. Say **\"start from the one problem\"** and we'll do that instead.",
    ].join("\n");
    setModuleMessages((prev) => ({
      ...prev,
      mission: [...(prev.mission || []), { role: "assistant", content: body }],
    }));
    setReviseFocusTrigger((n) => n + 1);
  }, [moduleOutputs, moduleMessages]);

  // ── Niche pivot ─────────────────────────────────────────────────────────────

  /* A niche change is worked in place in My Mission. Her existing thread there is
     an asset, not a liability — Brill should see the old answers to tell what
     carries forward. It just has to be told the niche moved. */
  const openNichePivotInMission = (incoming, outgoing) => {
    const oldLine = outgoing?.statement || outgoing?.label;
    const body = [
      `**Your niche is now: ${incoming.label}.**`,
      oldLine ? `\nYou were building for: *${oldLine}*` : null,
      "",
      "Let's rebuild the foundation of it together — the one problem, who you're for, the transformation promise, and your niche statement. We're keeping everything that's still true about how you work, and rebuilding only what was built around who you used to serve.",
      "",
      "Start here: what's the one problem you're solving now? Not a list — the one.",
    ].filter((l) => l !== null).join("\n");
    setModuleMessages((prev) => ({
      ...prev,
      mission: [...(prev.mission || []), { role: "assistant", content: body }],
    }));
  };

  const handleCreateNicheVersion = useCallback(async (label, statement) => {
    if (!user?.id) return;
    try {
      const sb = await getSupabase();
      const outgoing = nicheVersions.find((v) => v.is_active);
      const snapshot = buildNicheSnapshot(moduleOutputs);

      /* Snapshot the outgoing niche in its own words before it stops being
         active — that's what lets a later redo say "you used to serve X".
         Deactivating first also keeps the one-active-per-coach index happy. */
      if (outgoing) {
        await sb.from("niche_versions")
          .update({ snapshot, is_active: false })
          .eq("id", outgoing.id);
      }

      const { data: created, error } = await sb.from("niche_versions")
        .insert({
          clerk_user_id: user.id,
          label,
          statement: statement || null,
          is_active: true,
          activated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error || !created) {
        console.error("Failed to create niche version:", error);
        // Put the old niche back rather than leaving her with none active.
        if (outgoing) await sb.from("niche_versions").update({ is_active: true }).eq("id", outgoing.id);
        return;
      }

      setNicheVersions((prev) => [
        created,
        ...prev.map((v) => (v.id === outgoing?.id ? { ...v, is_active: false, snapshot } : v)),
      ]);
      openNichePivotInMission(created, outgoing);
      setActive("niche_review");
    } catch (err) {
      console.error("Failed to create niche version:", err);
    }
  }, [user?.id, nicheVersions, moduleOutputs]);

  const handleSwitchNiche = useCallback((versionId) => {
    const target = nicheVersions.find((v) => v.id === versionId);
    if (!target || !user?.id) return;
    setConfirmDialog({
      title: `Switch back to "${target.label}"?`,
      message: "This makes that niche active again. Nothing is deleted and nothing gets rewritten — anything built on your other niche will simply show as worth reviewing.",
      confirmLabel: "Switch back",
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const sb = await getSupabase();
          const outgoing = nicheVersions.find((v) => v.is_active);
          const snapshot = buildNicheSnapshot(moduleOutputs);
          if (outgoing) {
            await sb.from("niche_versions")
              .update({ snapshot, is_active: false })
              .eq("id", outgoing.id);
          }
          const activatedAt = new Date().toISOString();
          await sb.from("niche_versions")
            .update({ is_active: true, activated_at: activatedAt })
            .eq("id", versionId);
          setNicheVersions((prev) => prev.map((v) =>
            v.id === versionId ? { ...v, is_active: true, activated_at: activatedAt }
              : v.id === outgoing?.id ? { ...v, is_active: false, snapshot }
                : v
          ));
          setActive("niche_review");
        } catch (err) {
          console.error("Failed to switch niche:", err);
        }
      },
    });
  }, [user?.id, nicheVersions, moduleOutputs]);

  /* Renaming is not a pivot. Bootstrapped versions are labelled "My first niche"
     from nothing, so without this the only way to fix a label would be creating a
     new version — which would flag every locked section stale to change a word. */
  const handleRenameNiche = useCallback(async (versionId, label) => {
    const next = (label || "").trim();
    if (!next || !user?.id) return;
    setNicheVersions((prev) => prev.map((v) => (v.id === versionId ? { ...v, label: next } : v)));
    try {
      const sb = await getSupabase();
      await sb.from("niche_versions").update({ label: next }).eq("id", versionId);
    } catch (err) {
      console.error("Failed to rename niche:", err);
    }
  }, [user?.id]);

  /* "Still true" — she confirms the section survives the pivot. No regeneration:
     the content she already has becomes this niche's copy of it, so it stops
     reading as stale.

     IT COPIES FORWARD NOW, IT DOES NOT RESTAMP (Stage 3). The old line moved the
     row: `update ... set niche_version_id` on every row matching the module and
     section. Two things broke under the wider key. It would have set EVERY
     niche's copy of that section to the active id at once — several rows landing
     on one key, a unique violation, and there was no error check, so the button
     would have done nothing at all with nothing on screen to say so. And even
     for a single row, moving it takes the section out of the niche she built it
     under, which is the destruction Stage 3 exists to end.

     So: a new row for the active niche holding the same words. Her old niche
     keeps its copy, this one gains its own, and pressing the button twice is
     harmless — the second press conflicts with the first and updates it.

     It also records the section as cleared, which is the one thing a later
     rebuild of that module skips without asking. Everything else in the queue
     gets stopped at, including sections already on the current niche — silently
     stepping over those is what made a rebuild feel like it was jumping around.
     But she has just made this call explicitly, and putting it back in front of
     her would undo the point of the button. */
  const handleKeepSection = useCallback(async (moduleKey, sectionName) => {
    const nicheId = activeNiche?.id;
    if (!nicheId || !user?.id) return;
    const content = moduleOutputs[moduleKey]?.[sectionName];
    const priorStamp = outputStamps[moduleKey]?.[sectionName];

    /* Marking it cleared is the half that always applies — it is her saying "do
       not stop me at this one again", and it holds whether or not a row moves. */
    setClearedSections((prev) => ({
      ...prev,
      [moduleKey]: [...(prev[moduleKey] || []), sectionName],
    }));

    /* Nothing to copy forward: no stored content, or it is already this niche's.
       Writing anyway would either save an empty row — which sectionLockState
       correctly refuses to call a lock — or overwrite the current copy with
       whatever the resolver happened to be showing. */
    if (content == null || String(content).trim() === "" || priorStamp === nicheId) return;

    setOutputStamps((prev) => ({
      ...prev,
      [moduleKey]: { ...(prev[moduleKey] || {}), [sectionName]: nicheId },
    }));
    try {
      const sb = await getSupabase();
      const { error } = await sb.from("outputs").upsert({
        clerk_user_id: user.id, module_key: moduleKey, section_name: sectionName,
        content, niche_version_id: nicheId, updated_at: new Date().toISOString(),
      }, { onConflict: "clerk_user_id,module_key,section_name,niche_version_id" });
      /* Put the stamp back if it did not land. A section reading current while
         the database still calls it past is the same two-readers-disagree bug
         the lock work spent a week removing. */
      if (error) {
        console.error("Failed to confirm section:", error);
        setOutputStamps((prev) => restoreSectionValue(prev, moduleKey, sectionName, priorStamp));
      }
    } catch (err) {
      console.error("Failed to confirm section:", err);
      setOutputStamps((prev) => restoreSectionValue(prev, moduleKey, sectionName, priorStamp));
    }
  }, [user?.id, activeNiche?.id, moduleOutputs, outputStamps]);

  /* Defined here rather than next to the other rebuild handlers because it reads
     handleKeepSection, which is declared just above — a useCallback that closes
     over a `const` defined later in the same scope throws on first render. */
  const handleStartTriage = useCallback((moduleKey, keeps) => {
    setTriageModule(null);
    /* Copy each "still true" forward into the active niche so it stops reading
       as stale everywhere in the app, then hand the same list to the rebuild —
       the state write above won't have landed by the time it builds its queue.
       One upsert each: they are independent rows and a failed one reverts only
       its own stamp. */
    keeps.forEach((sectionName) => handleKeepSection(moduleKey, sectionName));
    handleRebuildModule(moduleKey, keeps);
  }, [handleKeepSection, handleRebuildModule]);

  /* "Redo" — the existing revise flow, opened with both niches named so Brill
     carries forward what still holds instead of starting from nothing. */
  const handleRedoSection = useCallback((moduleKey, sectionName) => {
    const current = moduleOutputs[moduleKey]?.[sectionName]
      || parseOutputs(moduleMessages[moduleKey] || [])[sectionName];
    const stampedTo = outputStamps[moduleKey]?.[sectionName];
    const builtOn = nicheVersions.find((v) => v.id === stampedTo)
      || nicheVersions.find((v) => !v.is_active);
    const oldNiche = builtOn?.statement || builtOn?.label;

    const body = [
      `**${sectionName}** was built on your old niche${oldNiche ? ` — *${oldNiche}*` : ""}.`,
      "",
      `You're building for **${activeNiche?.label}** now. We're going to keep what's still true — your method, your structure, your voice, anything that's about how you work rather than who you serve — and rebuild anywhere this was guessing based on who you used to think you were serving.`,
      "",
      current ? `Here's what you have now:\n\n${current}` : null,
      "",
      "Tell me what still holds and what doesn't — or just say \"go\" and I'll take a first pass and show you what I kept.",
    ].filter((l) => l !== null).join("\n");

    setRightPanel(null);
    setActive(moduleKey);
    setModuleMessages((prev) => ({
      ...prev,
      [moduleKey]: [...(prev[moduleKey] || []), { role: "assistant", content: body }],
    }));

    /* The preamble above is for her to read; it cannot instruct Brill. An assistant
       message reads as something Brill already said, so it fell back to the module's
       own script in the system prompt and answered "let me start fresh" — the exact
       opposite of the point. The instruction has to arrive in a user turn, so it
       goes through the promptTrigger path, which fires an API call with a hidden
       [PROMPT_START:] turn. */
    setPromptTrigger({
      moduleId: moduleKey,
      promptName: sectionName,
      ts: Date.now(),
      redo: {
        oldNiche: oldNiche || null,
        newNiche: activeNiche?.statement || activeNiche?.label || null,
      },
    });
  }, [moduleOutputs, moduleMessages, outputStamps, nicheVersions, activeNiche]);

  const handleViewBlueprint = (moduleId) => {
    setActive(moduleId);
    const msgs = moduleMessages[moduleId] || [];
    const outputs = parseOutputs(msgs);
    const prompts = MODULE_PROMPTS[moduleId] || [];
    setRightPanel({ type: "blueprint", moduleId, outputs, prompts });
  };

  const handleBlueprintReady = (moduleId) => {
    setBlueprints((prev) => ({ ...prev, [moduleId]: true }));
  };

  const buildIdeationBlueprintData = () => {
    const allOutputs = {};
    const allPromptNames = [];

    // Niche: prefer Decoder's locked output; fall back to the coach's first
    // substantive message in Offer Ideation (the input that seeded Mission Expander).
    const decoderOutputs = parseOutputs(moduleMessages.decoder || []);
    let niche = decoderOutputs["View Decoded Statement"];
    if (!niche) {
      const offerMsgs = moduleMessages.offer_ideation || [];
      const firstUserMsg = offerMsgs.find(
        (m) => m.role === "user" && !m.content.startsWith("[PROMPT_START:")
      );
      niche = firstUserMsg?.content || null;
    }
    if (niche) {
      allOutputs["Niche"] = niche;
      allPromptNames.push("Niche");
    }

    // Offer Ideation outputs in order. Named for its module: `offerOutputs` now
    // means the Accelerator's My Offer at component level, and these two are
    // different modules with an overlapping section name ("Pricing").
    const ideationOfferOutputs = parseOutputs(moduleMessages.offer_ideation || []);
    ["Mission Expander", "Course Creator", "Pricing"].forEach((p) => {
      if (ideationOfferOutputs[p]) {
        allOutputs[p] = ideationOfferOutputs[p];
        allPromptNames.push(p);
      }
    });

    return { outputs: allOutputs, prompts: allPromptNames };
  };

  const handleViewIdeationBlueprint = () => {
    if (!ideationTestimonialSubmitted) {
      setRightPanel({ type: "ideationTestimonialGate" });
    } else {
      const { outputs, prompts } = buildIdeationBlueprintData();
      setRightPanel({ type: "ideationBlueprint", outputs, prompts });
    }
  };

  const handleViewMasterBlueprint = () => {
    if (!testimonialSubmitted) {
      setRightPanel({ type: "testimonialGate" });
    } else {
      const allOutputs = {};
      const allPromptNames = [];
      ACCELERATOR_MODULES.forEach((id) => {
        const outputs = mergedOutputs(id);
        const prompts = MODULE_PROMPTS[id] || [];
        prompts.forEach((p) => {
          if (outputs[p]) {
            const key = `${MODULE_META[id].label} — ${p}`;
            allOutputs[key] = outputs[p];
            allPromptNames.push(key);
          }
        });
      });
      setRightPanel({ type: "masterBlueprint", outputs: allOutputs, prompts: allPromptNames });
    }
  };

  // Assemble the locked brand foundation the Social Generator writes from —
  // `mergedOutputs` gives each module's locked sections, and we pull only the ones
  // that shape voice/audience. Content Pillars seeds the picker.
  const buildSocialContext = () => {
    const wanted = {
      voice: ["Voice Profile", "Brand Feeling & Color Palette"],
      positioning: ["The One Line", "Elevator Pitch", "Positioning Statements"],
      client: ["Ideal Client Profile", "Tension & Transformation"],
      mission: ["Niche Statement", "The Transformation Promise", "Expanded Mission Statement"],
      story: ["My Archetype & My Personal Style", "What It Cost & What You Carried"],
    };
    const sections = {};
    Object.entries(wanted).forEach(([mod, names]) => {
      const out = mergedOutputs(mod);
      names.forEach((n) => { if (out[n]) sections[n] = out[n]; });
    });
    return {
      clientContext: {
        displayName: userSettings.displayName || user?.firstName || "",
        archetype: userSettings.archetype || "",
        archetypePair: userSettings.archetypePair || "",
        sections,
      },
      contentPillars: mergedOutputs("social")["Content Pillars"] || null,
    };
  };

  const renderRightPanel = () => {
    if (!rightPanel) return null;
    if (rightPanel.type === "testimonialGate") {
      return (
        <TestimonialGatePanel
          userId={user?.id}
          isMobile={isMobile}
          onClose={() => setRightPanel(null)}
          onUnlocked={() => {
            setTestimonialSubmitted(true);
            const allOutputs = {};
            const allPromptNames = [];
            ACCELERATOR_MODULES.forEach((id) => {
              const msgs = moduleMessages[id] || [];
              const outputs = parseOutputs(msgs);
              const prompts = MODULE_PROMPTS[id] || [];
              prompts.forEach((p) => {
                if (outputs[p]) {
                  const key = `${MODULE_META[id].label} — ${p}`;
                  allOutputs[key] = outputs[p];
                  allPromptNames.push(key);
                }
              });
            });
            setRightPanel({ type: "masterBlueprint", outputs: allOutputs, prompts: allPromptNames });
          }}
        />
      );
    }
    if (rightPanel.type === "masterBlueprint") {
      return <BlueprintPanel moduleLabel="Master Blueprint" outputs={rightPanel.outputs} promptNames={rightPanel.prompts} onClose={() => setRightPanel(null)} isMobile={isMobile} />;
    }
    if (rightPanel.type === "ideationTestimonialGate") {
      return (
        <TestimonialGatePanel
          userId={user?.id}
          isMobile={isMobile}
          onClose={() => setRightPanel(null)}
          headerLabel="Offer Ideation Blueprint"
          leadLine="Your Offer Ideation Blueprint is ready."
          description="Before we generate it — your experience matters here. A quick review of the masterclass unlocks the full Offer Ideation Blueprint compiled from your locked niche, mission, curriculum, and pricing."
          trigger="ideation_blueprint_gate"
          successMessage="Thank you. Your Offer Ideation Blueprint is generating now."
          onUnlocked={() => {
            setIdeationTestimonialSubmitted(true);
            const { outputs, prompts } = buildIdeationBlueprintData();
            setRightPanel({ type: "ideationBlueprint", outputs, prompts });
          }}
        />
      );
    }
    if (rightPanel.type === "ideationBlueprint") {
      return <BlueprintPanel moduleLabel="Offer Ideation Blueprint" outputs={rightPanel.outputs} promptNames={rightPanel.prompts} onClose={() => setRightPanel(null)} isMobile={isMobile} />;
    }
    const meta = MODULE_META[rightPanel.moduleId];
    if (rightPanel.type === "output") {
      // Which niche this was locked under, so she isn't guessing later.
      const stampId = outputStamps[rightPanel.moduleId]?.[rightPanel.promptName];
      const stampNiche = nicheVersions.find((v) => v.id === stampId);
      const nicheStamp = stampNiche ? {
        label: stampNiche.label,
        date: stampNiche.activated_at || stampNiche.created_at,
        stale: stampId !== activeNiche?.id,
      } : null;
      return <OutputPanel promptName={rightPanel.promptName} content={rightPanel.content} moduleLabel={meta.label} nicheStamp={nicheStamp} onClose={() => setRightPanel(null)} onRevise={() => handleRevisePrompt(rightPanel.moduleId, rightPanel.promptName)} isMobile={isMobile} />;
    }
    if (rightPanel.type === "blueprint") {
      return <BlueprintPanel moduleLabel={meta.label} outputs={rightPanel.outputs} promptNames={rightPanel.prompts} onClose={() => setRightPanel(null)} isMobile={isMobile} />;
    }
    return null;
  };

  return (
    <PanelModeProvider panelMode={panelMode} setPanelMode={changePanelMode}>
    <div className="brill-root" data-panel={panelMode} style={{ display: "flex", height: "100vh", overflow: "hidden", fontFamily: FONT_STACK }}>
      <style>{tokenCSS()}</style>
      <style>{`
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes slideIn { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeOverlay { from { opacity: 0; } to { opacity: 1; } }
        * { box-sizing: border-box; }
        /* SCROLLBARS, and why this looks over-specified.
           A 4px bar in #e0e0e0 was invisible on a light canvas, so panels that
           scrolled looked like panels with nothing below the fold. The 10px
           ::-webkit rules that replaced it did not fix it either, because
           CHROME IGNORES ::-webkit-scrollbar ENTIRELY once scrollbar-width or
           scrollbar-color is set on the element — and the old rule set
           scrollbar-width: thin on everything. So Chrome drew its own THIN bar
           with a TRANSPARENT track: a faint sliver, still effectively invisible.
           Hence: the standard properties carry the real design (auto width, a
           tinted track so the channel is visible even before you find the
           thumb), and the ::-webkit block is kept for Safari, which has no
           support for the standard ones. Keep the two in step.
           AND THEN WINDOWS 11 (her report, 2026-10-01: "the tiniest scroll bar
           ever … I can't even capture it"). With the standard properties set,
           Chrome on Windows 11 draws its own Fluent scrollbar: a hairline thumb
           that only widens under the pointer. So the standard properties now
           apply ONLY where ::-webkit-scrollbar does not exist (Firefox), and
           Chrome, Edge and Safari all take the bar below, always full width.
           GOOGLE'S WAY, her call the same day after a 22px grey bar read "too
           big and stark": no visible track, and a thumb that is a see-through
           tint of whatever it sits on — darker on the light canvas, lighter on
           dark (--scroll-thumb, a canvas token, so it follows the light /
           dark toggle; the always-dark sidebar sets its own). Grey-neutral,
           never teal: teal means press this (screen rule 5).
           THEN HER WHISPER BAR as the model ("visible but still sleek and
           unobtrusive"): a 12px pill inside a 21px gutter (8px read as
           "skinny again", 16px as "stark"), held 6px off the window's edge
           ("I think it's off the edge" — 3px read as cut off), a see-through fill,
           and a thin light STROKE (--scroll-thumb-stroke) for the glassy edge.
           The stroke is an inset shadow, which draws inside the transparent
           border — exactly round the visible pill. Phones keep 13px. */
        @supports not selector(::-webkit-scrollbar) {
          * { scrollbar-width: thin; scrollbar-color: var(--scroll-thumb) transparent; }
        }
        ::-webkit-scrollbar { width: ${isMobile ? 13 : 21}px; height: ${isMobile ? 13 : 21}px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: var(--scroll-thumb); border-radius: 9px; border: 3px solid transparent; ${isMobile ? "" : "border-right-width: 6px; border-bottom-width: 6px;"} background-clip: padding-box; box-shadow: inset 0 0 0 1px var(--scroll-thumb-stroke); min-height: 48px; }
        ::-webkit-scrollbar-thumb:hover { background: var(--scroll-thumb-hover); background-clip: padding-box; }
        textarea { -webkit-appearance: none; }
        html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
        html, body { height: 100%; overflow: hidden; ${isMobile ? 'position: fixed;' : ''} width: 100%; overscroll-behavior: none; -webkit-tap-highlight-color: transparent; }
        * { -webkit-tap-highlight-color: transparent; }
        button { touch-action: manipulation; user-select: none; -webkit-user-select: none; }
        a { -webkit-touch-callout: none; }
        @supports (height: 100dvh) { .brill-root { height: 100dvh !important; } }
      `}</style>

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* Triage sits above the sidebar in the tree but is driven from it. Rendered
          off staleModules rather than a snapshot, so marking something still true
          from anywhere else closes it rather than leaving a stale list on screen. */}
      {triageModule && staleModules.some((m) => m.moduleKey === triageModule) && (
        <NicheTriageDialog
          module={staleModules.find((m) => m.moduleKey === triageModule)}
          onClose={() => setTriageModule(null)}
          onStart={handleStartTriage}
        />
      )}

      {!isMobile && (
        <Sidebar active={active} onSelect={setActive} moduleMessages={moduleMessages} blueprints={blueprints}
          moduleOutputs={moduleOutputs} outputStamps={outputStamps} outputsLoaded={outputsLoaded}
          offers={offerStore.offers} momentumCounts={momentumCounts}
          isOpen={true} onClose={() => {}} isMobile={false}
          onViewOutput={handleViewOutput} onViewBlueprint={handleViewBlueprint}
          onStartPrompt={handleStartPrompt} onReviseSection={handleRevisePrompt}
          user={user} onSignOut={() => signOut()}
          onResetModule={handleResetModule} onResetAll={handleResetAll}
          onViewMasterBlueprint={handleViewMasterBlueprint}
          onViewIdeationBlueprint={handleViewIdeationBlueprint}
          activeNiche={activeNiche} nicheStaleCount={staleCounts.redo + staleCounts.mixed + staleCounts.check}
          nicheStaleByModule={staleByModule}
          nicheStaleTierByModule={staleTierByModule}
          onOpenNicheTriage={setTriageModule}
          onOpenNiche={() => setActive("niche")}
          askChats={{
            available: askChats.available,
            chats: askChats.chats,
            problem: askChats.problem,
            hasEarlier: (moduleMessages.chat || []).some((m) => m.created_at),
            activeKey: active === "chat" ? askKey : undefined,
          }}
          onNewAskChat={() => { newAskChat(); if (isMobile) setSidebarOpen(false); }}
          onOpenAskChat={(key) => { openAskChat(key); if (isMobile) setSidebarOpen(false); }}
          onRenameAskChat={askChats.rename}
          onDeleteAskChat={deleteAskChat}
          hasAcceleratorAccess={hasAcceleratorAccess} hasMomentumAccess={hasMomentumAccess} />
      )}

      {/* MOUNTED WHETHER OR NOT IT IS SHOWING. `Sidebar` already returns null
          when `isMobile && !isOpen`, so gating the element here as well did
          nothing for the look and unmounted the component every time she
          picked a tool — taking `expandedBuckets`, `expandedModules` and the
          rest of the tree's state with it. Her report, 2026-09-18: open Grow
          to reach Scripts, come back to the nav, and Grow has folded itself
          shut, so nothing says where she is.

          This is NOT the auto-expand that was deliberately removed above the
          `phase` effect in Sidebar. Arriving somewhere still only highlights
          it. This keeps an expansion SHE asked for by clicking.

          Staying mounted is safe because the one effect in there — the phase
          retarget — sits above that null return and keeps running, so the
          switcher still follows `active` while the sheet is down. */}
      {isMobile && (
        <Sidebar active={active} onSelect={setActive} moduleMessages={moduleMessages} blueprints={blueprints}
          moduleOutputs={moduleOutputs} outputStamps={outputStamps} outputsLoaded={outputsLoaded}
          offers={offerStore.offers} momentumCounts={momentumCounts}
          isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isMobile={true} phaseRequest={phaseRequest}
          onViewOutput={(id, p, c) => { handleViewOutput(id, p, c); setSidebarOpen(false); }}
          onViewBlueprint={(id) => { handleViewBlueprint(id); setSidebarOpen(false); }}
          onStartPrompt={(id, p) => { handleStartPrompt(id, p); setSidebarOpen(false); }}
          onReviseSection={(id, p) => { handleRevisePrompt(id, p); setSidebarOpen(false); }}
          user={user} onSignOut={() => signOut()}
          onResetModule={(id) => { handleResetModule(id); setSidebarOpen(false); }}
          onResetAll={() => { handleResetAll(); setSidebarOpen(false); }}
          onViewMasterBlueprint={() => { handleViewMasterBlueprint(); setSidebarOpen(false); }}
          onViewIdeationBlueprint={() => { handleViewIdeationBlueprint(); setSidebarOpen(false); }}
          activeNiche={activeNiche} nicheStaleCount={staleCounts.redo + staleCounts.mixed + staleCounts.check}
          nicheStaleByModule={staleByModule}
          nicheStaleTierByModule={staleTierByModule}
          onOpenNicheTriage={setTriageModule}
          onOpenNiche={() => { setActive("niche"); setSidebarOpen(false); }}
          askChats={{
            available: askChats.available,
            chats: askChats.chats,
            problem: askChats.problem,
            hasEarlier: (moduleMessages.chat || []).some((m) => m.created_at),
            activeKey: active === "chat" ? askKey : undefined,
          }}
          onNewAskChat={() => { newAskChat(); if (isMobile) setSidebarOpen(false); }}
          onOpenAskChat={(key) => { openAskChat(key); if (isMobile) setSidebarOpen(false); }}
          onRenameAskChat={askChats.rename}
          onDeleteAskChat={deleteAskChat}
          hasAcceleratorAccess={hasAcceleratorAccess} hasMomentumAccess={hasMomentumAccess} />
      )}

      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, overflow: "hidden" }}>
      {/* Credits: the two weeks' notice, running low, or out. Nothing otherwise. */}
      <CreditsBanner />
      <main style={{ flex: 1, overflow: "hidden", display: "flex", minWidth: 0 }}>
        {!hasAcceleratorAccess && ACCELERATOR_MODULES.includes(active) ? (
          <LockedModulePanel moduleId={active} isMobile={isMobile} onMenuOpen={() => setSidebarOpen(true)} />
        ) : !hasMomentumAccess && MOMENTUM_MODULES.includes(active) ? (
          <LockedModulePanel
            moduleId={active} isMobile={isMobile} onMenuOpen={() => setSidebarOpen(true)}
            lockedMessage="Momentum is the next step beyond the Accelerator — your toolkit for delivering, creating, and growing once your foundation is built."
            lockedCTA={null}
          />
        ) : (active?.startsWith("m/") || active === "momentum_hub" || active === "social_generator") ? (
          <MomentumHub
            /* Social is a Momentum panel since 2026-09-24 (spec phase A1). The nav
               row keeps its old id, so the sidebar, the phase gate and anything
               that sends her to "social_generator" still land here. */
            panel={active === "momentum_hub" ? "offer_ladder" : active === "social_generator" ? "social" : active.slice(2)}
            socialContext={active === "social_generator" ? buildSocialContext() : null}
            isAdmin={isAdminEmail(user?.primaryEmailAddress?.emailAddress)}
            isMobile={isMobile}
            websiteOutputs={websiteOutputs}
            /* The Offer Ladder pulls her locked Signature Offer from this. */
            offerOutputs={offerOutputs}
            /* Course seeds its modules from her locked Curriculum, which lives in
               My Offer — see utils/useCourseModules.js. */
            courseSeeded={courseSeeded}
            markCourseSeeded={markCourseSeeded}
            settingsLoaded={settingsLoaded}
            /* For the ladder's drafting chat. Merged (saved over parsed-from-history)
               rather than parsed-only, so a section she locked before this session
               still counts — and scoped down to what the guide names by
               CONTEXT_SCOPES in utils/momentumContext.js. */
            acceleratorOutputs={ACCELERATOR_MODULES.reduce((acc, id) => {
              const out = mergedOutputs(id);
              if (Object.keys(out).length > 0) acc[id] = out;
              return acc;
            }, {})}
            activeNiche={activeNiche}
            outputStamps={outputStamps}
            userSettings={userSettings}
            /* The whole useOffers() return. App owns it so the sidebar's Momentum
               count and the ladder always read the same array. */
            offerStore={offerStore}
            onMenuOpen={() => setSidebarOpen(true)}
            onOpenModule={(id) => setActive(id)}
          />
        ) : MOMENTUM_PLACEHOLDER_MODULES[active] ? (
          <PlaceholderModulePanel moduleId={active} isMobile={isMobile} onMenuOpen={() => setSidebarOpen(true)} />
        ) : active === "archetype_results" ? (
          <ArchetypeResultsPanel isMobile={isMobile} onMenuOpen={() => setSidebarOpen(true)} userSettings={userSettings} saveSettings={saveSettings} />
        ) : ME_SCREENS.includes(active) ? (
          /* Every Me screen inside ONE frame: the section bar with the menu,
             the Library and light/dark (components/MeFrame.jsx). */
          <MeFrame isMobile={isMobile} onMenuOpen={() => setSidebarOpen(true)} onLibrary={() => setActive(navIdFor("library"))}>
            {active === "me_voice" || active === "me_writing" ? (
              <WritingPanel writing={writing} onSave={saveWriting} isMobile={isMobile} />
            ) : active === "me_business" ? (
              <BusinessPanel userSettings={userSettings} saveSettings={saveSettings}
                onSaveWebsite={(v) => saveAboutField("website", v)}
                onSaveSignoff={(v) => saveAboutField("signoff", v)} isMobile={isMobile} />
            ) : active === "me_bio" ? (
              <BioPanel bio={userSettings.bio} onSave={(v) => saveAboutField("bio", v)} isMobile={isMobile} />
            ) : active === "me_skills" ? (
              <SkillsPanel skills={skills} onSave={saveSkills} isMobile={isMobile} />
            ) : (
              <BrandPanel userSettings={userSettings} saveSettings={saveSettings} onSaveBrand={saveBrand}
                voiceOutputs={mergedOutputs("voice")} isMobile={isMobile} />
            )}
          </MeFrame>
        ) : active === "admin" ? (
          <AdminPanel
            isMobile={isMobile}
            onMenuOpen={() => setSidebarOpen(true)}
            onBack={() => setActive("chat")}
          />
        ) : active === "niche" ? (
          <NichePanel
            nicheVersions={nicheVersions}
            activeNiche={activeNiche}
            staleCounts={staleCounts}
            isMobile={isMobile}
            onBack={() => setActive("chat")}
            onCreateVersion={handleCreateNicheVersion}
            onSwitchTo={handleSwitchNiche}
            onRename={handleRenameNiche}
            onOpenMission={handleReviseNicheStatement}
            onViewReview={() => setActive("niche_review")}
          />
        ) : active === "niche_review" ? (
          <NicheReviewPanel
            modules={staleModules}
            activeNiche={activeNiche}
            previousNiche={previousNiche}
            isMobile={isMobile}
            onBack={() => setActive("niche")}
            onRedo={handleRedoSection}
            onRebuild={handleStartTriage}
          />
        ) : active === "settings" ? (
          <SettingsPanel
            userSettings={userSettings}
            saveSettings={saveSettings}
            user={user}
            /* Erase everything is hers alone — see the note in SettingsPanel. */
            canEraseEverything={isAdminEmail(user?.primaryEmailAddress?.emailAddress)}
            isMobile={isMobile}
            onBack={() => setActive("chat")}
            onResetAll={handleResetAll}
          />
        ) : (
          <ChatPanel
            /* One instance per Ask Brill chat, so switching chats starts clean.
               Without 0042 there is one thread, as before. */
            key={active === "chat" && askChats.available ? `chat:${askKey || "new"}` : active} moduleId={active}
            messages={active === "chat" && askChats.available
              ? (askKey === null ? [] : askKey === EARLIER ? (moduleMessages.chat || []) : (askThreads[askKey] || []))
              : (moduleMessages[active] || [])}
            setMessages={active === "chat" && askChats.available
              ? setAskMessagesFor(askKey === null ? EARLIER : askKey)
              : (msgs) => setModuleMessages((prev) => ({ ...prev, [active]: msgs }))}
            askChat={active === "chat" && askChats.available ? {
              key: askKey,
              title: askChats.chats.find((c) => c.id === askKey)?.title,
              mode: askChats.chats.find((c) => c.id === askKey)?.mode || "chat",
              onRename: askChats.rename,
              onDelete: deleteAskChat,
              start: startAskChat,
              setterFor: setAskMessagesFor,
              conversationIdFor: (key) => conversationIdFor(user?.id, key),
              touch: askChats.touch,
              problem: askChats.problem,
              leftOff,
              firstName: user?.firstName || "",
              onStartSection: handleStartPrompt,
            } : null}
            loading={moduleLoading[active] || false}
            setLoading={(val) => setModuleLoading((prev) => ({ ...prev, [active]: val }))}
            onBlueprintReady={handleBlueprintReady}
            onViewBlueprint={handleViewBlueprint}
            onViewMasterBlueprint={handleViewMasterBlueprint}
            onViewIdeationBlueprint={handleViewIdeationBlueprint}
            onSwitchModule={setActive}
            isMobile={isMobile} onMenuOpen={() => setSidebarOpen(true)}
            rightPanel={renderRightPanel()} onClosePanel={() => setRightPanel(null)}
            onViewOutput={handleViewOutput}
            savedOutputs={moduleOutputs[active] || {}}
            foundationOutputs={active === "chat" ? foundationOutputs : null}
            coachOffers={active === "chat" ? offerStore.offers : null}
            brillTools={active === "chat" ? brillTools : null}
            outputsLoaded={outputsLoaded}
            onOutputLocked={handleOutputLocked}
            /* Only this module's failure. A strip in My Client naming a section
               of My Offer would be worse than silence — she cannot act on it
               from where she is standing. */
            lockFailure={lockFailure && lockFailure.moduleKey === active ? lockFailure : null}
            onRetryLock={() => {
              if (!lockFailure) return;
              const { moduleKey, sectionName, content } = lockFailure;
              setLockFailure(null);
              handleOutputLocked(moduleKey, sectionName, content);
            }}
            onDismissLockFailure={() => setLockFailure(null)}
            onReviseSection={(name) => handleRevisePrompt(active, name)}
            reviseFocusTrigger={reviseFocusTrigger}
            reviseTarget={reviseTarget && !reviseTarget.sent && reviseTarget.moduleId === active ? reviseTarget.promptName : null}
            /* TWO QUESTIONS, NOT ONE (2026-08-27). `reviseTarget` answers "does
               the next message carry a revision instruction" — true once, then
               spent. `reviseOpen` answers "is a revision still unresolved",
               which stays true until it locks. Gating the exits on the first one
               hid them for exactly one keystroke: she pressed Revise, typed
               "let's change it", and "Let's go to Offer Ideation" was back
               underneath Brill asking what to change. */
            reviseOpen={reviseTarget && reviseTarget.moduleId === active ? reviseTarget.promptName : null}
            onReviseHandled={() => setReviseTarget((t) => (t ? { ...t, sent: true } : null))}
            userId={user?.id}
            testimonialSubmitted={testimonialSubmitted}
            onTestimonialSubmitted={() => setTestimonialSubmitted(true)}
            historyLoadFailed={historyLoadFailed}
            /* Clearing dataLoaded re-runs the history effect; a failed load
               applied nothing, so there is nothing to merge over. Send stays
               paused until the reload succeeds (see the effect). */
            onRetryHistory={() => setDataLoaded(false)}
            userSettings={userSettings}
            promptTrigger={promptTrigger}
            onPromptTriggered={() => setPromptTrigger(null)}
            onStartPrompt={handleStartPrompt}
            acceleratorOutputs={ACCELERATOR_MODULES.reduce((acc, id) => {
              const outputs = parseOutputs(moduleMessages[id] || []);
              if (Object.keys(outputs).length > 0) acc[id] = outputs;
              return acc;
            }, {})}
            ideationOutputs={IDEATION_CONTEXT_EXPORTERS.reduce((acc, id) => {
              const outputs = parseOutputs(moduleMessages[id] || []);
              if (Object.keys(outputs).length > 0) acc[id] = outputs;
              return acc;
            }, {})}
            activeNiche={activeNiche}
            outputStamps={outputStamps}
            /* Offered only in the module she just finished, so it cannot follow
               her around the app. The next module is the first one still stale in
               review order, which is the order the review screen listed. */
            rebuildNext={rebuildNext?.from === active
              ? staleModules.find((m) => m.moduleKey !== rebuildNext.from) || null
              : null}
            onRebuildNext={(moduleKey) => { setRebuildNext(null); setTriageModule(moduleKey); }}
            onDismissRebuildNext={() => setRebuildNext(null)}
            brandAsk={brandAsk?.moduleKey === active ? brandAsk : null}
            onBrandAnswer={(yes) => { const a = brandAsk; setBrandAsk(null); if (yes && a) saveBrand(a.next); }}
          />
        )}
      </main>
      <InstallBanner isMobile={isMobile} />
      {isMobile && <BottomNav active={active} onSelect={setActive} onOpenSidebar={(phase) => { setSidebarOpen(true); if (phase) setPhaseRequest({ phase }); }} moduleMessages={moduleMessages} hasMomentumAccess={hasMomentumAccess} />}
      </div>
    </div>
    </PanelModeProvider>
  );
}

// ─── APP (auth gate) ──────────────────────────────────────────────────────────

// Access requires a granted phase from the GHL→Clerk webhook. The admin account
// is always allowed so Tevra is never locked out.
const VALID_PHASES = ["ideation", "accelerator", "momentum"];

export default function App() {
  const { isLoaded, isSignedIn, user } = useUser();

  if (!isLoaded) {
    return (
      <div style={{ minHeight: "100vh", background: C.indigo, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {[0, 150, 300].map((d) => (
            <div key={d} style={{ width: 10, height: 10, borderRadius: "50%", background: C.tealLight, animation: "bounce 1s infinite", animationDelay: `${d}ms` }} />
          ))}
        </div>
        <style>{`@keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }`}</style>
      </div>
    );
  }

  if (!isSignedIn) return <LoginScreen />;

  const phase = user?.publicMetadata?.phase;
  const isAdmin = isAdminEmail(user?.primaryEmailAddress?.emailAddress);
  if (!isAdmin && !VALID_PHASES.includes(phase)) return <NoAccessScreen />;

  // Everyone agrees to the current terms before Brill opens — there is no sign-up
  // form to put a checkbox on, since the GHL webhook creates accounts for people.
  if (user?.publicMetadata?.termsVersion !== TERMS_VERSION) return <ConsentScreen />;

  return <BrillApp />;
}
